import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./card"
import { Button } from "./button";
import { Input } from "./input";
import { Checkbox } from './checkbox';
import { Label } from "./label"
import { useToast } from "./use-toast";

import { useNavigate, useLocation } from 'react-router-dom';
import MapComponent from './MapComponent';
import ActivityFilterPanel from './ActivityFilterPanel';
import StravaComponent from './stravacomponent';
import GarminComponent from './garmincomponent';
import IntegratedFrameSettings from './IntegratedFrameSettings';
import GPXUploadHandler from './gpx-handler';
import { ImageSlideshow } from './imageslideshow';
import StaticImageSlideshow from './staticimageslideshow';
import BasicStylesGrid from './BasicStylesGrid';
import StylePreview from './StylePreview';
import { getBasicStyle } from '../utils/basicStyles';
import FormatSelectorPopup from './formatselector';
import ColorSettingsPopup from './ColorSettingsPopup';
import ColorLegend from './ColorLegend';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./Select";
import { AlertTriangle, CheckCircle2, XCircle, X, Maximize2, Loader2, Upload, MapPin, Hand, Plus, Minus, Scaling, Palette, Map as MapIcon, Frame, History, Activity, Image as ImageIcon, Check, Unlock, ArrowLeft, ArrowRight, Mountain, Touchpad, SlidersHorizontal, Lightbulb, Mail, Star } from "lucide-react"
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { motion, AnimatePresence } from "framer-motion"
import MapPurchaseSection from './mappurchuase';
import AdminPanel from './adminpanel';
import { MobileMapLayout } from './mobilepurchase';
import ReviewForm from './ReviewForm';
import polyline from '@mapbox/polyline';

import StravaPoweredLogo2 from './img/api_logo_pwrdBy_strava_stack_light.svg'
import TwoFingerZoomImg from './img/twofingerzoom.png'
import GarminLogo from './img/garmin_logo.png'
import borderExample from './img/border_example.png';

import { PRICES } from '../config/prices';

import { CSS4_COLORS } from './mplcolors';
import { FORMATS } from '../config/prices';
import { Slider } from './slider';
import { trackActivityEvents, trackGenerationEvents, trackPurchaseEvents, trackMapConfigEvents } from '../utils/ga4Events';
import { DEFAULT_BASIC_STYLE_ID, BASIC_ONLY_KM2_THRESHOLD, formatKm2 } from '../utils/basicStyles';

const DEFAULT_MAP_SETTINGS = {
  km2: 500,
  figure_width: 80,
  figure_height: 60,
  // Intentionally empty: a stale persisted name doesn't match the current
  // map state after manual drags, so we always start blank and let the user
  // fill it via search. Last-generated map keeps its own copy in
  // recent_images.place_name for the print label.
  placeName: "",
  // Render mode + style.
  // - 'features' (default for new renders): the existing QGIS topo + per-
  //   layer OSM features path, with full color/opacity controls.
  // - 'basic': a basemap tile layer is the entire background; only
  //   activities are user-customizable. This is the only available mode
  //   when km² > BASIC_ONLY_KM2_THRESHOLD.
  renderMode: 'features',
  selectedStyle: DEFAULT_BASIC_STYLE_ID,
  activities: { color: 'orange', alpha: 0.85, active: true, lineWidth: 0.8, lineWidthAuto: true, byTypeEnabled: false, byType: {}, mode: 'heatmap' },
  buildings: { color: 'darkgrey', alpha: 0.8, active: true },
  forests: { color: 'forestgreen', alpha: 0.1, active: true },
  naturalareas: { color: 'darkgreen', alpha: 0.2, active: true },
  water: { color: 'cornflowerblue', alpha: 0.4, active: true },
  oceans: { color: 'royalblue', alpha: 0.4, active: true },
  streets: { color: 'black', alpha: 0.2, active: true, details: 'roads' },
  land: { color: 'palegoldenrod', alpha: 0.15, active: true }
};

const DEFAULT_FRAME_SETTINGS = {
  addText: false,
  textPosition: 'bottom',
  frameColor: '#ffffff',
  frameSize: 2,
  fontColorTop: '#000000',
  fontColorBottom: '#808080',
  coordinateFormat: 'decimal',
  framePercentage: 10,
  frameWidth: 0,
  line1Items: [],
  line2Items: [],
  availableItems: [
    { id: 'coordinates_dms', content: 'Coordinates (DMS)' },
    { id: 'coordinates_metric', content: 'Coordinates (Metric)' },
    { id: 'place_name', content: 'Place Name' },
    { id: 'custom_text', content: 'Custom Text' },
    { id: 'distance', content: 'Distance' },
    { id: 'hours', content: 'Hours' },
    { id: 'elevation', content: 'Elevation' }
  ]
};

const DEFAULT_FORMAT = {
  value: '4:3',
  size: 'S',
  label: '4:3 S',
  dimensions: { width: 28, height: 21 },
  aspectRatio: 1.333
};

// Master switch for the (currently broken) custom-frame feature. When false:
//   - the "Frame" tab is hidden from the section nav
//   - the generate payload always sends frame=false / no frameSettings
//   - any saved addFrame=true is ignored on load
// Flip to true to re-enable once the frame compositing is fixed.
const FRAME_ENABLED = false;

// Static helper text that rotates below the progress bar every 5 s while a
// render is in flight. Keeps the user informed during the multi-minute waits
// without needing per-phase backend coordination — these are general truths
// about the render pipeline.
const RENDER_TIPS = [
  'If some data needs to be downloaded for this image — it might take a little longer ⏳',
  'If Buildings 🏢 or Streets 🛣️ are enabled, processing needs more time.',
  'You can safely switch tabs or leave this page — the render keeps going 👌',
  'Upon first generation of a new area, data needs to be downloaded, this might need a little longer 🫣',
];

// Format an elapsed duration since `started` (ISO string, ms-epoch, or
// Date) as "30s" / "1m" / "1m 30s". Returns "" on bad input so the
// caller can render the surrounding text without leaking "NaN".
const formatElapsedSince = (started) => {
  if (!started) return '';
  const t = typeof started === 'string' ? Date.parse(started) : started;
  if (!Number.isFinite(t)) return '';
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
};

// Progress bar + label fed by the polled `generationStatus`. Used in the
// preview section to render server-side render progress; survives reloads
// because the underlying state lives in the user record on the backend.
const GenerationProgress = ({ status }) => {
  const isVisible = !!status && status.state !== 'idle' && status.state !== 'done';
  const isError = status?.state === 'error';
  const [tipIndex, setTipIndex] = useState(0);
  const [, setElapsedTick] = useState(0);

  // Rotate every 5 s only while the bar is visible and the render hasn't
  // errored — on error we replace the tip area with the error message and
  // there's no point cycling. Resets to 0 each time the bar reappears so
  // the first tip the user sees is the most informative one.
  useEffect(() => {
    if (!isVisible || isError) {
      setTipIndex(0);
      return undefined;
    }
    const id = setInterval(() => {
      setTipIndex((i) => (i + 1) % RENDER_TIPS.length);
    }, 5000);
    return () => clearInterval(id);
  }, [isVisible, isError]);

  // 1 s ticker just for the "running for Xs" suffix on the label. Lives
  // here (not at the dashboard level) so the rest of the dashboard
  // doesn't re-render every second while a render is active.
  useEffect(() => {
    if (!isVisible || isError) return undefined;
    const id = setInterval(() => setElapsedTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [isVisible, isError]);

  if (!isVisible) return null;
  const pct = Math.max(0, Math.min(100, status.progress || 0));
  const elapsed = !isError ? formatElapsedSince(status.started_at) : '';
  return (
    <div className="w-full max-w-xs mx-auto flex flex-col items-center space-y-2">
      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${isError ? 'bg-red-500' : 'bg-gradient-to-r from-black/90 via-black to-black/70'}`}
          style={{ width: `${isError ? 100 : pct}%` }}
        />
      </div>
      <div className="text-xs text-gray-700 text-center">
        {isError
          ? (status.error || 'Generation failed')
          : `${status.label || 'Working…'} (${pct}%)`}
      </div>
      {!isError && elapsed && (
        <div className="text-xs text-gray-500 text-center">
          running for {elapsed}
        </div>
      )}
      {!isError && (
        // key forces React to remount the node so the fade-in animation
        // replays on every tip swap; min-height keeps the bar from jumping
        // when tips of different lengths cycle in.
        <div
          key={tipIndex}
          className="text-xs text-gray-500 text-center italic min-h-[2.75em] px-2 animate-in fade-in-0 duration-500"
        >
          {RENDER_TIPS[tipIndex]}
        </div>
      )}
    </div>
  );
};

// Section-aware Tipps overlay. Each section gets its own list of tips with
// optional Show-Me actions. The shell (header / footer / styling) is shared
// across sections so the visual stays consistent.
const TippsOverlay = ({
  section,
  onClose,
  onNavigate,
  onFlashMap,
  onFlashCompare,
  onShowActivityPanel,
  onShowActivityCustomize,
  onFlashActivityFilter,
  hasActivities,
  activitySettings,
  visibleActivityCount,
}) => {
  const tips = buildTipsForSection(section, {
    onClose,
    onNavigate,
    onFlashMap,
    onFlashCompare,
    onShowActivityPanel,
    onShowActivityCustomize,
    onFlashActivityFilter,
    hasActivities,
    activitySettings,
    visibleActivityCount,
  });

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-lg w-full mx-4 relative shadow-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-5 pt-4 pb-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            <h3 className="text-lg font-semibold">Tipps</h3>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {tips.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-4">
              No tips for this section yet.
            </p>
          )}
          {tips.map((tip, i) => (
            <div
              key={i}
              className={`flex items-start justify-between gap-3 ${tip.warn ? 'p-3 rounded-xl bg-amber-50 border border-amber-200' : ''
                } ${tip.indent ? 'pl-6 border-l-2 border-gray-200 ml-1' : ''} ${tip.dimmed ? 'opacity-50' : ''
                } ${tip.mobileOnly ? 'lg:hidden' : ''}`}
            >
              <p className="text-sm text-gray-700 flex-1">{tip.text}</p>
              {tip.action && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-2xl whitespace-nowrap"
                  onClick={tip.action}
                  disabled={tip.dimmed}
                >
                  {tip.actionLabel || 'Show me'}
                </Button>
              )}
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex items-center gap-2 text-sm text-gray-600 flex-shrink-0">
          <Mail className="h-4 w-4 text-gray-500 flex-shrink-0" />
          <span>
            Any problems or suggestions? Write me at{' '}
            <a
              href="mailto:info@mapprints.de"
              className="font-medium text-blue-600 hover:underline"
            >
              info@mapprints.de
            </a>
          </span>
        </div>
      </div>
    </div>
  );
};

// Returns the tip list for a given section. Each tip is either purely
// informational (no `action`) or carries a Show-Me callback. Show-Me for
// controls without dedicated flash plumbing just navigates to the section
// — the controls themselves are visible on that section's main view.
const buildTipsForSection = (section, ctx) => {
  const {
    onClose,
    onNavigate,
    onFlashMap,
    onFlashCompare,
    onShowActivityPanel,
    onShowActivityCustomize,
    onFlashActivityFilter,
    hasActivities,
    activitySettings,
    visibleActivityCount,
  } = ctx;

  const goTo = (target) => {
    onClose();
    if (target) onNavigate(target);
  };

  // Helper for Show-Me on map controls: switch to map section if the user
  // is elsewhere, then trigger the flash slightly later so the control has
  // mounted before the highlight runs.
  const showOnMap = (controlName) => () => {
    onClose();
    if (section !== 'map') onNavigate('map');
    onFlashMap(controlName);
  };

  if (section === 'map') {
    // Order: location → rectangle → km² → format → add activities → (indented)
    // width → (indented) find activity. Width and Find Activity only make
    // sense with activities loaded, so they're greyed when none are visible.
    return [
      {
        text: <>Search a location via the <span className="font-semibold">search bar</span> at the top.</>,
        action: showOnMap('location'), actionLabel: 'Show me'
      },
      {
        text: <>Use the <span className="font-semibold">rectangle</span> button to fit the map view to your print rectangle.</>,
        action: showOnMap('rectangle'), actionLabel: 'Show me'
      },
      {
        text: <>Resize the print rectangle with the <span className="font-semibold">km²</span> button.</>,
        action: showOnMap('km2'), actionLabel: 'Show me'
      },
      {
        text: <>Pick the print aspect ratio with the <span className="font-semibold">format</span> button (4:3, 3:4, 16:9, …).</>,
        action: showOnMap('format'), actionLabel: 'Show me'
      },
      {
        text: <>Add activities by clicking the <span className="font-semibold">Strava</span> or <span className="font-semibold">Garmin</span> connect buttons in the activity panel.</>,
        action: () => { onClose(); if (section !== 'map') onNavigate('map'); onShowActivityPanel?.(); },
        actionLabel: 'Show me'
      },
      // Width + Find Activity belong to the activity flow — visually grouped
      // under "Add activities" with a left border and indent. Disabled when
      // no activities are visible because both controls are no-ops without
      // any polylines on the map.
      {
        text: <>Adjust activity line thickness with the <span className="font-semibold">Width</span> button — <span className="font-semibold">Auto</span> picks a sensible value based on activity count. In <span className="font-semibold">Heatmap</span> mode the same chip becomes an opacity slider.</>,
        action: showOnMap('width'), actionLabel: 'Show me',
        indent: true, dimmed: !hasActivities
      },
      {
        text: <>Switch between <span className="font-semibold">Lines</span> and <span className="font-semibold">Heatmap</span> via the segmented control next to <span className="font-semibold">Width</span>. Heatmap merges every visible activity into a Strava-style frequency map; brighter pixels = paths you've run more often.</>,
        indent: true, dimmed: !hasActivities
      },
      {
        text: <>Pick the heatmap <span className="font-semibold">palette</span> (orange, red, blue, green, or purple) in the <span className="font-semibold">Activity style</span> card on the right panel — the gradient is built around your chosen color.</>,
        indent: true, dimmed: !hasActivities
      },
      {
        text: <>Filter the period — pick a preset or a custom range from the <span className="font-semibold">Date</span> chip in the activity panel.</>,
        action: () => { onClose(); onFlashActivityFilter?.('date'); },
        actionLabel: 'Show me',
        indent: true, dimmed: !hasActivities
      },
      {
        text: <>Enable / disable whole activity types (Run, Ride, Hike…) via the <span className="font-semibold">type checkboxes</span> in <span className="font-semibold">Activities in view</span>.</>,
        action: () => { onClose(); onFlashActivityFilter?.('types'); },
        actionLabel: 'Show me',
        indent: true, dimmed: !hasActivities
      },
      {
        text: <>Find a specific activity and re-center the map on it via the activity <span className="font-semibold">search</span>.</>,
        action: showOnMap('finder'), actionLabel: 'Show me',
        indent: true, dimmed: !hasActivities
      },
      {
        text: <>On phones the map fills the screen, so a stray touch can drag the rectangle while you're trying to scroll. Use the <span className="font-semibold">Lock map</span> chip on the map's left edge to freeze it (and unlock it the same way).</>,
        action: showOnMap('lock'), actionLabel: 'Show me',
        mobileOnly: true,
      },
    ];
  }

  if (section === 'style') {
    return [
      { text: <><span className="font-semibold">Basic</span> styles use a tile-stitched basemap and work for any area size, including very large ones. <span className="font-semibold">Fully customizable</span> styles render every feature (forests, water, buildings, …) — only available below the size limit.</> },
      { text: <>Pick a curated bundled style or try one shared by another user — user-shared styles are marked with a small icon. Hover for the author's name and how often it's been used.</> },
      { text: <>Activities can plot either as individual <span className="font-semibold">Classic lines</span> or a frequency <span className="font-semibold">Heatmap</span> — toggle at the top of the right-hand legend. Lines color per activity type; heatmap merges everything into one Strava-style frequency surface.</> },
      { text: <>In <span className="font-semibold">Heatmap</span> mode, open the <span className="font-semibold">Customize</span> sheet → <span className="font-semibold">Activities</span> card to pick a palette (orange, red, blue, green, purple). The gradient is auto-built from your chosen base color.</> },
      { text: <>The <span className="font-semibold">Customize</span> button lets you toggle layers (forests, water, buildings, …) and tweak each one's color and opacity. <span className="font-semibold">Post-process</span> below the legend offers two looks: 🖌 Hand-painted paper or 🎨 Watercolored.</> },
    ];
  }

  if (section === 'preview') {
    const tips = [];

    tips.push({
      text: <>Not seeing your activities? Go to the <span className="font-semibold">Map</span> section and increase <span className="font-semibold">Width</span>, or set it to <span className="font-semibold">Auto</span>.</>,
      action: () => { onClose(); onNavigate('map'); onFlashMap('width'); },
      actionLabel: 'Show me',
    });

    // Conditional warning: if the user's activity opacity is low or their
    // manual line width is low for a small activity set, the heatmap may be
    // invisible on the preview. Show Me jumps straight into the Activities
    // category in the Style → Customize panel where the controls live.
    const alpha = activitySettings?.alpha;
    const lineWidth = activitySettings?.lineWidth;
    const lineWidthAuto = activitySettings?.lineWidthAuto === true;
    const lowOpacity = typeof alpha === 'number' && alpha < 0.3;
    const lowManualWidth = !lineWidthAuto && typeof lineWidth === 'number' && lineWidth < 0.5;
    const fewActivities = visibleActivityCount > 0 && visibleActivityCount < 5;
    if (lowOpacity || (lowManualWidth && fewActivities)) {
      const reasons = [];
      if (lowOpacity) reasons.push('opacity is very low');
      if (lowManualWidth && fewActivities) reasons.push('width is low for only a handful of activities');
      tips.push({
        warn: true,
        text: <>Heads up — your activities might be hard to see because the {reasons.join(' and ')}. Open the activity customize section to adjust them.</>,
        action: () => { onClose(); onShowActivityCustomize?.(); },
        actionLabel: 'Show me',
      });
    }

    // Compare goes last per spec — it's an extra feature, not a fix.
    tips.push({
      text: <>You can compare up to <span className="font-semibold">3 images</span> using the <span className="font-semibold">Compare</span> button.</>,
      action: () => { onClose(); onFlashCompare(); },
      actionLabel: 'Show me',
    });

    return tips;
  }

  if (section === 'frame') {
    return [
      { text: <>Toggle <span className="font-semibold">Add a Frame</span> to wrap your map with a border, optional title, distance/elevation lines, and a date.</> },
      { text: <>The frame preview on the right updates as you change settings; it shows the same area you'll get on print.</> },
    ];
  }

  return [
    {
      text: <>Pick a section above to see its specific tips.</>,
      action: () => goTo('map'), actionLabel: 'Go to Map'
    },
  ];
};

// The native date picker emits a Date at local-midnight. When we ship that
// to the backend filter as the *upper* bound, an activity recorded later
// today (e.g. 10:00) is excluded because midnight precedes 10:00. Bump to
// end-of-day for `to` and start-of-day for `from` before serializing so
// the user's "today" picks include the entire day.
const dateBoundaryISO = (d, kind) => {
  if (!d) return null;
  const out = new Date(d);
  if (kind === 'end') {
    out.setHours(23, 59, 59, 999);
  } else {
    out.setHours(0, 0, 0, 0);
  }
  return out.toISOString();
};

// Two rectangles are "similar enough" to reuse a cached max_dpis table when
// their km² match within `areaTolerance` and their centers sit within
// `distanceKm`. The DPI table mostly tracks (a) the QGIS pixel-size cliff
// (function of size+dpi only — same everywhere) and (b) global topography-
// data availability, which doesn't change appreciably across moderate
// distances. So a translated rectangle of similar size yields the same caps.
const rectangleBoundsAreSimilar = (
  prevBounds,
  currBounds,
  prevKm2,
  currKm2,
  { distanceKm = 200, areaTolerance = 0.1 } = {},
) => {
  if (!prevBounds || !currBounds) return false;
  if (prevBounds.length !== 2 || currBounds.length !== 2) return false;
  if (prevKm2 == null || currKm2 == null || prevKm2 <= 0) return false;
  if (Math.abs(prevKm2 - currKm2) / prevKm2 > areaTolerance) return false;

  const center = (b) => [(b[0][0] + b[1][0]) / 2, (b[0][1] + b[1][1]) / 2];
  const [aLat, aLon] = center(prevBounds);
  const [bLat, bLon] = center(currBounds);
  const toRad = (deg) => (deg * Math.PI) / 180;
  const R = 6371; // earth radius in km
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  const distance = 2 * R * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return distance <= distanceKm;
};

export default function Dashboard({ userData, onLogout, selectedTab, setSelectedTab, onAdminStatus }) {
  const [user, setUser] = useState(null);
  const [file, setFile] = useState(null);
  const [genrationError, setGenerationError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stravaStatus, setStravaStatus] = useState({
    isConnected: false,
    numActivities: 0,
    activities: null,
    error: null,
    isLoading: false,
    lastSync: null,
  });
  const [garminStatus, setGarminStatus] = useState({
    connected: false,
    isLoading: false,
    activities: null,
    lastSync: null,
    error: null,
    activityCount: 0,
    currentProgress: 0,
    expectedCount: 0
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  // Snapshot of the inputs at the last successful generation. Persisted to
  // localStorage so the "no changed settings" guard on the Generate button
  // survives page reloads — without this, a fresh mount would treat every
  // current settings value as "different from null" and re-enable the button.
  const [lastGeneratedSettings, setLastGeneratedSettings] = useState(() => {
    try {
      const raw = localStorage.getItem('lastGeneratedSettings');
      return raw ? JSON.parse(raw) : null;
    } catch (_e) {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (lastGeneratedSettings) {
        localStorage.setItem('lastGeneratedSettings', JSON.stringify(lastGeneratedSettings));
      } else {
        localStorage.removeItem('lastGeneratedSettings');
      }
    } catch (_e) { /* quota / private mode — no-op */ }
  }, [lastGeneratedSettings]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_MAP_SETTINGS);
  const [selectedFormat, setSelectedFormat] = useState(DEFAULT_FORMAT);
  const [isPortrait, setIsPortrait] = useState(false);
  const [mapCenter, setMapCenter] = useState([42.0, 2.83]);
  const [rectangleBounds, setRectangleBounds] = useState([[41.95, 2.78], [42.05, 2.88]]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [allDates, setAllDates] = useState(true);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d;
  });
  const [toDate, setToDate] = useState(new Date());
  const topography = true; // Always on: backend map generation requires topography in every print.
  // Holds a deferred style apply when the chosen style would flip the
  // activity render mode (heatmap ↔ classic lines). User picks via a
  // confirmation dialog whether to keep the current mode or adopt the
  // style's mode. null = no pending decision.
  const [pendingStyleApply, setPendingStyleApply] = useState(null);
  // Pending activity-mode flip from the side-by-side picker in the
  // styles legend. Holds the target mode ('polylines' | 'heatmap') the
  // user clicked; confirming in the dialog actually applies it.
  const [pendingModeFlip, setPendingModeFlip] = useState(null);
  const [mapStyle, setMapStyle] = useState('satellite'); // preview tile layer: 'standard' | 'topo' | 'satellite'
  const [addFrame, setAddFrame] = useState(false);
  const [frameSettings, setFrameSettings] = useState(DEFAULT_FRAME_SETTINGS);
  const [activitySource, setActivitySource] = useState('none');
  const [activitiesInView, setActivitiesInView] = useState([]);
  const [hiddenActivityIds, setHiddenActivityIds] = useState([]);
  // Activities that should appear on the Basic-tab live preview. Same
  // shape MapComponent uses (`{ id, type, coordinates, ... }`), so the
  // preview can color per-type via getActivityTypeColor and match what
  // the user already sees on the dashboard map.
  const basicPreviewActivities = useMemo(
    () => activitiesInView.filter(
      (a) => !hiddenActivityIds.includes(a.id)
        && Array.isArray(a.coordinates)
        && a.coordinates.length > 1,
    ),
    [activitiesInView, hiddenActivityIds],
  );
  const [syncWaterOceans, setSyncWaterOceans] = useState(false);

  const [commutes, setCommutes] = useState(false);

  const [enabledActivities, setEnabledActivities] = useState({});
  const [garminActivities, setGarminActivities] = useState({});

  // Per-source filter actually sent to the backend at generate time. For
  // Strava/Garmin this is keyed by activity TYPE ("Ride", "Run", …); for
  // manual it's keyed by individual id. Hidden-by-id activities are sent
  // separately as `hiddenActivityIds` so the backend can drop them after
  // type filtering — that's what makes the activity panel's "disable all
  // rides" toggle propagate to the generated image.
  const effectiveActivityFilter = useMemo(() => {
    return activitySource === 'strava' ? filteredActivities :
      activitySource === 'garmin' ? garminActivities :
        enabledActivities;
  }, [activitySource, filteredActivities, garminActivities, enabledActivities]);
  const [totalEligibleActivities, setTotalEligibleActivities] = useState(0);
  // Recent maps state lifted out of MobileMapLayout so the data survives tab
  // toggles (the layout component is unmounted whenever the user is on the
  // Map / Frame / Style tabs). Fetched once on first preview-tab visit and
  // refreshed after each Generate-map success (see effect on finalImage).
  const [recentMapsData, setRecentMapsData] = useState({ maps: [] });
  const [isLoadingRecentMaps, setIsLoadingRecentMaps] = useState(false);
  const [recentMapsError, setRecentMapsError] = useState(null);
  const [recentMapsFetched, setRecentMapsFetched] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    showCommute: false,
  });
  const [manualActivities, setManualActivities] = useState([]);
  const [isLoadingManual, setIsLoadingManual] = useState(false);
  const [isLoadingFrame, setIsLoadingFrame] = useState(false);

  const [isUnlocked, setIsUnlocked] = useState(false);
  const [finalImage, setFinalImage] = useState(null);
  const [frameImage, setFrameImage] = useState(null);
  const [isGeneratingFinal, setIsGeneratingFinal] = useState(false);
  // True between clicking Abort and the next status poll seeing the
  // render gone. Disables the Abort button and changes its label so
  // the user doesn't fire repeated POSTs while the worker unwinds.
  const [isAborting, setIsAborting] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  // Persistent server-side generation state — polled via
  // /user/generation-status. Survives reloads and dedupes across tabs.
  // Shape: { state: 'idle'|'running'|'done'|'error', label, progress, image_id?, error? }
  const [generationStatus, setGenerationStatus] = useState(null);
  const [isFormatSelectorOpen, setIsFormatSelectorOpen] = useState(false);
  const [voucher, setVoucher] = useState('');

  const { toast } = useToast();
  const [images, setImages] = useState([]);
  const [imageUrls, setImageUrls] = useState({});

  const [activeMapSection, setActiveMapSection] = React.useState('map');
  // Tipps overlay (modal hints + "Show me" actions). Bumping the *FlashNonce
  // counters fires a 2s border-flash on the target button so the user can
  // locate it after the overlay closes.
  const [tippsOpen, setTippsOpen] = useState(false);
  // Single Tipps-driven map flash target. The Map section's "Show me" buttons
  // bump this with the right control name so MapComponent flashes the matching
  // border. Compare lives outside MapComponent so it keeps its own nonce.
  const [mapFlashTarget, setMapFlashTarget] = useState({ name: null, nonce: 0 });
  const [compareFlashNonce, setCompareFlashNonce] = useState(0);
  const flashMapControl = (name) =>
    setMapFlashTarget((prev) => ({ name, nonce: prev.nonce + 1 }));

  // The blue "Plot activities" affordance is rendered in dashboard.js (not in
  // MapComponent), so it can't ride the mapFlashTarget mechanism. Track its
  // flash separately. The Tipps "Show me" for "Add activities" closes the
  // panel first so the floating button is visible, then flashes it.
  const [plotActivitiesFlashing, setPlotActivitiesFlashing] = useState(false);
  const flashPlotActivities = () => {
    setActivitiesPanelOpen(false);
    setPlotActivitiesFlashing(true);
    setTimeout(() => setPlotActivitiesFlashing(false), 2000);
  };
  // Flash the source-switcher's "No activities" button when the user clicks
  // "Show me" on the disabled-activities hint inside the Style → Customize
  // panel. We have to close customize + navigate to the Map section + open
  // the activities panel first so the button is actually visible.
  const [noActivitiesFlashing, setNoActivitiesFlashing] = useState(false);
  const flashNoActivities = () => {
    setCustomizeOpen(false);
    handleSectionChange('map');
    setActivitiesPanelOpen(true);
    setTimeout(() => {
      setNoActivitiesFlashing(true);
      setTimeout(() => setNoActivitiesFlashing(false), 2000);
    }, 150);
  };
  // Flash targets inside ActivityFilterPanel (the right-side panel).
  // Tipps "Show me" buttons for "Filter dates" / "Enable/disable types"
  // call flashActivityFilter('date'|'types') — we navigate to the map
  // section, open the activities panel so the filter is visible, then
  // bump the corresponding flash flag for 2s.
  const [dateFilterFlashing, setDateFilterFlashing] = useState(false);
  const [typeFilterFlashing, setTypeFilterFlashing] = useState(false);
  const flashActivityFilter = (target) => {
    handleSectionChange('map');
    setActivitiesPanelOpen(true);
    setTimeout(() => {
      if (target === 'date') {
        setDateFilterFlashing(true);
        setTimeout(() => setDateFilterFlashing(false), 2000);
      } else if (target === 'types') {
        setTypeFilterFlashing(true);
        setTimeout(() => setTypeFilterFlashing(false), 2000);
      }
    }, 150);
  };
  const [customizeOpen, setCustomizeOpen] = useState(false);
  // Independent of settings.renderMode so the user can VIEW the Features tab
  // even when their km² has forced the persisted renderMode to 'basic'. In
  // that case we render Features content with a blurry overlay explaining
  // the size limit. Default mirrors whatever was last persisted.
  const [styleTabView, setStyleTabView] = useState(
    settings?.renderMode === 'basic' ? 'basic' : 'features'
  );
  const [expandedCategory, setExpandedCategory] = useState(null);
  // Which side-panel is currently shown next to the map. Legacy: this
  // controlled Location / Format / Size / Activities tabs. After the
  // restructure, only the Activities panel is shown here, and the user can
  // collapse it so the map uses the full width.
  const [rightPanel, setRightPanel] = useState(() => {
    try {
      return localStorage.getItem('rightPanel') || 'location';
    } catch {
      return 'format';
    }
  });
  const [activitiesPanelOpen, setActivitiesPanelOpen] = useState(() => {
    try {
      return localStorage.getItem('activitiesPanelOpen') === 'true';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try { localStorage.setItem('activitiesPanelOpen', String(activitiesPanelOpen)); } catch (_e) { }
  }, [activitiesPanelOpen]);
  // Recent place searches are server-owned; we hydrate from backend on mount
  // and round-trip each new selection. localStorage is used only as an
  // optimistic cache so the chips show up before the fetch completes.
  const [recentPlaces, setRecentPlaces] = useState(() => {
    try {
      const stored = localStorage.getItem('recentPlaces');
      return stored ? JSON.parse(stored).slice(0, 5) : [];
    } catch (_e) {
      return [];
    }
  });
  const [customRatioW, setCustomRatioW] = useState('');
  const [customRatioH, setCustomRatioH] = useState('');

  const cacheRecentPlaces = (places) => {
    try { localStorage.setItem('recentPlaces', JSON.stringify(places)); } catch (_e) { }
  };

  const rememberRecentPlace = async (name) => {
    if (!name) return;
    setRecentPlaces(prev => {
      const next = [name, ...prev.filter(p => p !== name)].slice(0, 5);
      cacheRecentPlaces(next);
      return next;
    });
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const url = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
      const response = await fetch(`${url}/user/recent-places`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ place: name }),
      });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data.places)) {
          setRecentPlaces(data.places);
          cacheRecentPlaces(data.places);
        }
      }
    } catch (_e) {
      // Non-fatal: the optimistic cache keeps the UI functional.
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const url = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';
    fetch(`${url}/user/recent-places`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data && Array.isArray(data.places)) {
          setRecentPlaces(data.places);
          cacheRecentPlaces(data.places);
        }
      })
      .catch(() => { });
  }, []);
  const navigate = useNavigate();
  const location = useLocation();

  const mapSections = [
    { id: 'map', label: 'Map', icon: <MapIcon className="h-6 w-6" /> },
    { id: 'style', label: 'Style', icon: <Palette className="h-6 w-6" /> },
    ...(FRAME_ENABLED ? [{ id: 'frame', label: 'Frame', icon: <Frame className="h-6 w-6" /> }] : []),
    { id: 'preview', label: 'Preview', icon: <ImageIcon className="h-6 w-6" /> }
  ];
  const isValidMapSection = (id) => mapSections.some((s) => s.id === id);
  const [shouldCenter, setShouldCenter] = useState(false);
  const [shouldZoom, setShouldZoom] = useState(false);
  // Place-search "Set" auto-fit: we hand a lat/lon rectangle to the map and
  // bump the nonce; MapComponent's external ImperativeZoom fitBounds() it.
  const [fitToBounds, setFitToBounds] = useState(null);
  const [fitToBoundsNonce, setFitToBoundsNonce] = useState(0);
  // Yellow boundary flash (5s) — uses Nominatim's polygon_geojson when the
  // place has one (so Munich is the city outline, NZ is the coastline) and
  // falls back to the bbox for queries that only return a point.
  const [placeHighlightGeoJSON, setPlaceHighlightGeoJSON] = useState(null);
  const [placeHighlightNonce, setPlaceHighlightNonce] = useState(0);
  const [zoom, setZoom] = useState(10);
  // Bumping this nonce tells MapComponent to imperatively setView to the
  // current mapCenter + zoom. Used after loadSettings to restore the saved
  // position (MapContainer otherwise latches its initial center/zoom on
  // mount and ignores later prop changes).
  const [restoreViewNonce, setRestoreViewNonce] = useState(0);
  const initStravaChecks = useRef(false);
  // Blocks the localStorage save effect until loadSettings has completed —
  // without this gate the effect fires with defaults on first paint and
  // overwrites the saved snapshot before we've had a chance to read it.
  const settingsLoadedRef = useRef(false);
  // Mirror of settingsLoadedRef as reactive state. Used to delay mounting
  // the leaflet map until restored `mapCenter` / `zoom` / `rectangleBounds`
  // are in state — MapContainer only reads center/zoom at mount, so
  // mounting with defaults then updating props doesn't actually move the
  // map view. Gating here avoids the Girona flash-and-stick on reload.
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [maxDpis, setMaxDpis] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

  const handleMapCenterChange = (newCenter) => {
    setMapCenter(newCenter);
  };

  // Activity polyline fetching used to live inside MapComponent, which meant
  // the auto-width recalc only ran while the Map section was mounted. Hoisting
  // it here ensures activitiesInView (and the auto-width derived from it) is
  // up to date in Preview/Style/Frame too — otherwise a Generate from Preview
  // could ship the stale default width.
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const activityErrorShownRef = useRef(false);
  const fetchActivityPolylines = useCallback(async (bounds) => {
    if (settings?.activities?.active === false) return;
    if (!activitySource || activitySource === 'none') {
      setActivitiesInView([]);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;

    setIsLoadingActivities(true);
    try {
      const body = {
        bounds,
        margin_km: 100,
        sources: [activitySource],
        include_commutes: activitySource !== 'manual' && commutes,
      };
      if (!allDates) {
        body.date_range = {
          start: dateBoundaryISO(fromDate, 'start'),
          end: dateBoundaryISO(toDate, 'end'),
        };
      }
      const response = await fetch(`${API_URL}/user/activities/polylines`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const decoded = data.activities
        .map((activity) => {
          const coords = activity.polyline
            ? polyline.decode(activity.polyline).map(([lat, lng]) => [lat, lng])
            : [];
          return {
            id: activity.id,
            name: activity.name,
            type: activity.type,
            coordinates: coords,
            distance: activity.distance,
            elevation: activity.total_elevation_gain,
            startDate: activity.start_date,
          };
        })
        .filter((a) => a.coordinates.length >= 2);
      setActivitiesInView(decoded);
      activityErrorShownRef.current = false;
    } catch (error) {
      console.error('Error fetching activities:', error);
      if (!activityErrorShownRef.current) {
        activityErrorShownRef.current = true;
        toast({
          title: "Couldn't load activities",
          description: 'Your activity overlay may be out of date.',
          variant: 'destructive',
          duration: 3000,
        });
      }
    } finally {
      setIsLoadingActivities(false);
    }
  }, [activitySource, commutes, allDates, fromDate, toDate, settings?.activities?.active, API_URL, toast]);

  // Filter/rectangle-driven fetch: keeps activitiesInView in sync regardless
  // of which section is mounted. The Map section additionally drives a fetch
  // with the leaflet view bounds via onMapBoundsChange, so panning still
  // surfaces activities that fall outside the print rectangle.
  const lastFetchKeyRef = useRef('');
  useEffect(() => {
    if (!settingsLoaded) return;
    if (!rectangleBounds || rectangleBounds.length !== 2) return;
    const [a, b] = rectangleBounds;
    const minLat = Math.min(a[0], b[0]);
    const maxLat = Math.max(a[0], b[0]);
    const minLon = Math.min(a[1], b[1]);
    const maxLon = Math.max(a[1], b[1]);
    // Coalesce closely-spaced bounds updates (e.g. live rectangle drag) so we
    // don't spam the endpoint while preserving the existing 500ms debounce
    // pattern from MapComponent's ActivityFetchHandler.
    const key = JSON.stringify({ activitySource, commutes, allDates, fromDate, toDate, minLat, maxLat, minLon, maxLon, active: settings?.activities?.active });
    if (key === lastFetchKeyRef.current) return;
    lastFetchKeyRef.current = key;
    const handle = setTimeout(() => {
      fetchActivityPolylines({ minLat, maxLat, minLon, maxLon });
    }, 300);
    return () => clearTimeout(handle);
  }, [activitySource, commutes, allDates, fromDate, toDate, rectangleBounds, settings?.activities?.active, settingsLoaded, fetchActivityPolylines]);

  // Auto-mode line width: derive from activity count whenever activities or
  // hidden-id set change. Lives at dashboard level (mirrors what used to be
  // inside MapComponent) so the recalc runs in every section, not only Map.
  useEffect(() => {
    if (settings?.activities?.lineWidthAuto !== true) return;
    const hidden = new Set(hiddenActivityIds || []);
    let count = 0;
    for (const a of activitiesInView) if (!hidden.has(a.id)) count += 1;
    const suggested = count <= 0
      ? 0.8
      : Math.max(0.05, Math.round((2.4 / Math.pow(count, 0.4)) * 100) / 100);
    if (settings.activities?.lineWidth === suggested) return;
    setSettings((prev) => ({
      ...prev,
      activities: { ...prev.activities, lineWidth: suggested },
    }));
  }, [activitiesInView, hiddenActivityIds, settings?.activities?.lineWidthAuto, settings?.activities?.lineWidth]);

  // useEffect(() => {
  //   const params = new URLSearchParams(window.location.search);
  //   const tab = params.get('tab');
  //   if (tab) {
  //     setSelectedTab(tab);
  //   }
  // }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    const sectionParam = params.get('section');
    const storedSection = localStorage.getItem('activeMapSection');

    if (tabParam) {
      setSelectedTab(tabParam);
    }

    // Guard against legacy section ids (e.g. `place`, `activities`) left in
    // URLs or localStorage from before the section reorder — unknown ids
    // would render nothing and the dashboard would look "empty".
    if (sectionParam && isValidMapSection(sectionParam)) {
      setActiveMapSection(sectionParam);
    } else if (storedSection && isValidMapSection(storedSection)) {
      setActiveMapSection(storedSection);
      // Only update URL if there's no section param
      if (!sectionParam) {
        navigate(`${location.pathname}?section=${storedSection}`, { replace: true });
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('activeMapSection', activeMapSection);
  }, [activeMapSection]);

  useEffect(() => {
    localStorage.setItem('rightPanel', rightPanel);
  }, [rightPanel]);

  const handleSectionChange = (newSection) => {
    setActiveMapSection(newSection);
    localStorage.setItem('activeMapSection', newSection);

    const params = new URLSearchParams(location.search);
    params.set('section', newSection);

    // Preserve existing tab parameter if it exists
    const tabParam = params.get('tab');
    if (tabParam) {
      params.set('tab', tabParam);
    }

    navigate(`${location.pathname}?${params.toString()}`);
  };

  const handleTabChange = (newTab) => {
    setSelectedTab(newTab);

    const params = new URLSearchParams(location.search);
    params.set('tab', newTab);

    // Preserve section parameter
    params.set('section', activeMapSection);

    navigate(`${location.pathname}?${params.toString()}`);
  };

  const applySettings = (data) => {
    if (!data) {
      return;
    }

    // Set all individual states with null checks
    if (data.settings && typeof data.settings === 'object' && Object.keys(data.settings).length > 0) {
      // Merge over defaults so new fields (e.g. byTypeEnabled) don't disappear
      // if the saved snapshot predates them. placeName is force-cleared
      // because the saved value may not match the current map view (manual
      // drags don't update it), so we'd rather show a blank pill than
      // mislead the user with a stale label.
      // activities is deep-merged because the top-level spread would otherwise
      // replace the default activities object entirely — wiping new keys like
      // lineWidthAuto for users whose saved snapshot predates them.
      setSettings({
        ...DEFAULT_MAP_SETTINGS,
        ...data.settings,
        activities: {
          ...DEFAULT_MAP_SETTINGS.activities,
          ...(data.settings.activities || {}),
        },
        placeName: '',
      });
    }
    if (data.selectedFormat) setSelectedFormat(data.selectedFormat || DEFAULT_FORMAT);
    if (data.isPortrait !== undefined) setIsPortrait(data.isPortrait);
    if (data.mapCenter) setMapCenter(data.mapCenter);
    if (typeof data.zoom === 'number') setZoom(data.zoom);
    if (data.rectangleBounds) setRectangleBounds(data.rectangleBounds);
    if (data.activitySource) setActivitySource(data.activitySource);
    if (data.filteredActivities) setFilteredActivities(data.filteredActivities);
    if (data.enabledActivities) setEnabledActivities(data.enabledActivities);
    if (data.garminActivities) setGarminActivities(data.garminActivities);
    if (data.allDates !== undefined) setAllDates(data.allDates);
    if (data.dateFrom) setFromDate(new Date(data.dateFrom));
    if (data.dateTo) setToDate(new Date(data.dateTo));
    // topography is always on now; ignore any saved preference.
    // Force-off if the feature is disabled — even if a saved snapshot has
    // addFrame=true, we don't want it leaking into the generate payload.
    if (data.frame !== undefined) setAddFrame(FRAME_ENABLED ? data.frame : false);
    if (data.syncWaterOceans) setSyncWaterOceans(data.syncWaterOceans);
    if (data.frameSettings) setFrameSettings(data.frameSettings || DEFAULT_FRAME_SETTINGS);
    if (Array.isArray(data.hiddenActivityIds)) setHiddenActivityIds(data.hiddenActivityIds);
    if (typeof data.commutes === 'boolean') setCommutes(data.commutes);
    // URL `?section=` wins over the saved snapshot, so only apply these when
    // the URL hasn't already supplied a value.
    const urlParams = new URLSearchParams(window.location.search);
    if (data.activeMapSection && isValidMapSection(data.activeMapSection) && !urlParams.get('section')) {
      setActiveMapSection(data.activeMapSection);
    }
    if (data.rightPanel) setRightPanel(data.rightPanel);

    // if (data.settings.km2 === 0) {
    //   setTopography(false);
    // }
  };

  useEffect(() => {
    const loadSettings = async () => {
      // localStorage is the primary source — written synchronously on every
      // state change (see the save effect below). Backend is the fallback
      // for fresh devices / cleared storage.
      try {
        const localData = localStorage.getItem('dashboardSettings');
        if (localData) {
          const parsed = JSON.parse(localData);
          if (parsed?.frameSettings) parsed.frameSettings.addText = false;
          applySettings(parsed);
          if (parsed.rectangleBounds && parsed.rectangleBounds.length === 2) {
            const bounds = parsed.rectangleBounds;
            const centerLat = (bounds[0][0] + bounds[1][0]) / 2;
            const centerLon = (bounds[0][1] + bounds[1][1]) / 2;
            setMapCenter([centerLat, centerLon]);
          }
          // Bump the view-restore nonce so MapComponent imperatively moves
          // the leaflet map to the restored center + zoom. MapContainer
          // only honors its center/zoom props at initial mount, so without
          // this nudge the view stays at the Girona default.
          setRestoreViewNonce((n) => n + 1);
          return;
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "An error occured during loading settings",
          variant: "destructive",
          duration: 3000,
        });
      }

      try {
        const response = await fetch(`${API_URL}/user/settings`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const backendData = await response.json();
          if (backendData?.frameSettings) backendData.frameSettings.addText = false;
          applySettings(backendData);
          if (backendData.rectangleBounds && backendData.rectangleBounds.length === 2) {
            const bounds = backendData.rectangleBounds;
            const centerLat = (bounds[0][0] + bounds[1][0]) / 2;
            const centerLon = (bounds[0][1] + bounds[1][1]) / 2;
            setMapCenter([centerLat, centerLon]);
          }
          setRestoreViewNonce((n) => n + 1);
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "An error occured during loading settings",
          variant: "destructive",
          duration: 3000,
        });
      }
    };

    loadSettings().finally(() => {
      // Release the save gate once we've either applied saved data or
      // confirmed there was none. setState calls in applySettings are batched
      // and flush before this runs, so the save effect will fire with the
      // restored values rather than the initial defaults.
      settingsLoadedRef.current = true;
      setSettingsLoaded(true);
    });
  }, []);

  useEffect(() => {
    // Fetch user status when component mounts

    const checkAdminStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/user/status`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        // Log the raw response for debugging
        const rawResponse = await response.text();
        // console.log('Raw response:', rawResponse);

        // Try to parse if it looks like JSON
        try {
          const data = JSON.parse(rawResponse);
          const admin = data.is_admin || false;
          setIsAdmin(admin);
          if (onAdminStatus) onAdminStatus(admin);
        } catch (parseError) {
          setIsAdmin(false);
          if (onAdminStatus) onAdminStatus(false);
        }

      } catch (error) {
        setIsAdmin(false);
        if (onAdminStatus) onAdminStatus(false);
      }
    };

    checkAdminStatus();
  }, []);

  // Persist the full dashboard snapshot to localStorage on every change.
  // The gate ensures this doesn't fire with defaults before loadSettings has
  // had a chance to restore the last session.
  useEffect(() => {
    if (!settingsLoadedRef.current) return;
    const currentSettings = {
      settings,
      selectedFormat,
      isPortrait,
      mapCenter,
      zoom,
      rectangleBounds,
      activitySource,
      filteredActivities,
      enabledActivities,
      garminActivities,
      allDates,
      dateFrom: dateBoundaryISO(fromDate, 'start'),
      dateTo: dateBoundaryISO(toDate, 'end'),
      topography,
      frame: FRAME_ENABLED ? addFrame : false,
      frameSettings,
      syncWaterOceans,
      activeMapSection,
      rightPanel,
      hiddenActivityIds,
      commutes,
    };
    try {
      localStorage.setItem('dashboardSettings', JSON.stringify(currentSettings));
    } catch {
      // Quota exceeded or storage disabled — fall through, backend save
      // still runs on Generate.
    }
  }, [
    settings,
    selectedFormat,
    isPortrait,
    mapCenter,
    zoom,
    rectangleBounds,
    filteredActivities,
    enabledActivities,
    garminActivities,
    allDates,
    fromDate,
    toDate,
    topography,
    addFrame,
    frameSettings,
    activitySource,
    syncWaterOceans,
    activeMapSection,
    rightPanel,
    hiddenActivityIds,
    commutes,
  ]);

  const saveToBackend = async (finalDimensions) => {
    const settingsToSave = {
      settings: {
        ...settings,
        ...finalDimensions
      },
      selectedFormat,
      isPortrait,
      mapCenter,
      rectangleBounds,
      filteredActivities,
      enabledActivities,
      garminActivities,
      allDates,
      dateFrom: allDates ? null : dateBoundaryISO(fromDate, 'start'),
      dateTo: allDates ? null : dateBoundaryISO(toDate, 'end'),
      topography,
      frame: FRAME_ENABLED ? addFrame : false,
      frameSettings,
      activitySource,
    };

    await fetch(`${API_URL}/user/settings`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(settingsToSave)
    });
  };

  // const [stravaStatus, setStravaStatus] = useState({
  //   isConnected: false,
  //   numActivities: 0,
  //   activities: null,
  //   error: null,
  //   isLoading: false,
  //   lastSync: null,
  // });

  useEffect(() => {
    let pollingInterval;
    let attemptCount = 0;
    const MAX_ATTEMPTS = 2;

    const checkStravaData = async () => {
      try {
        const response = await fetch(`${API_URL}/user/strava-data-status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        // If we get a 405, stop polling and set not connected
        if (response.status === 405) {
          setStravaStatus(prev => ({
            ...prev,
            isConnected: false,
            isLoading: false,
            lastSync: null,
            activities: null,
            numActivities: 0,
          }));
          return;
        }

        const data = await response.json();

        setStravaStatus(prev => ({
          isConnected: data.isConnected,
          isLoading: data.isLoading,
          activities: data.activities,
          numActivities: data.numActivities,
          lastSync: data.lastSync || (data.activities ? new Date().toISOString() : null),
        }));

      } catch (error) {
        setStravaStatus(prev => ({
          ...prev,
          error: "Error fetching Strava data",
          isLoading: false,
        }));
      }
    };

    // Initial userData checks
    if (userData) {
      initStravaChecks.current = true;
      // Do two checks with small delay
      checkStravaData();
      setTimeout(checkStravaData, 1000);
      return;
    }
  }, []);

  const checkStravaStatus = () => {
    setStravaStatus(prev => ({
      ...prev, isLoading: true,
    }));
  };

  const handleSyncWaterOceans = (checked) => {
    setSyncWaterOceans(checked);
  };

  const handleActivitySourceChange = (source) => {
    setActivitySource(source);
    if (source === 'none') {
      settings.activities.active = false;
    } else {
      settings.activities.active = true;
    }
    // if (source === 'manual') {
    //   fetchManualActivities();
    // }
  };

  const fetchManualActivities = async () => {
    if (!userData) return;

    setIsLoadingManual(true);
    try {
      const response = await fetch(`${API_URL}/user/manual-activities`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setManualActivities(Array.isArray(data.activities) ? data.activities : []);
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occured loading your GPX Files",
        variant: "destructive",
        duration: 3000,
      });
      setManualActivities([]);
    } finally {
      setIsLoadingManual(false);
    }
  };

  useEffect(() => {
    if (activitySource === 'manual') {
      fetchManualActivities();
    }
  }, [activitySource]);

  const toggleFrame = () => {
    setAddFrame(!addFrame);
  };

  const handleFormatSelect = (format) => {
    // Store original dimensions from the incoming format
    // console.log(format)

    // Calculate dimensions based on portrait mode
    const currentDimensions = isPortrait
      ? {
        figure_width: format.dimensions.height,
        figure_height: format.dimensions.width
      }
      : {
        figure_width: format.dimensions.width,
        figure_height: format.dimensions.height
      };

    // Update settings with the correct dimensions
    setSettings(prev => ({
      ...prev,
      ...currentDimensions
    }));

    // Calculate aspect ratio based on portrait mode
    const aspectRatio = isPortrait
      ? format.dimensions.height / format.dimensions.width
      : format.dimensions.width / format.dimensions.height;

    // Create the complete format object
    setSelectedFormat({
      ...format,
      aspectRatio,
      currentDimensions
    });
  };

  const togglePortrait = () => {
    if (!selectedFormat?.dimensions) return;

    const newIsPortrait = !isPortrait;
    setIsPortrait(newIsPortrait);

    // Get the original dimensions
    const { width, height } = selectedFormat.dimensions;

    // Create new dimensions based on portrait mode
    const newDimensions = newIsPortrait
      ? {
        figure_width: height,
        figure_height: width
      }
      : {
        figure_width: width,
        figure_height: height
      };

    // Update settings with new dimensions
    setSettings(prev => ({
      ...prev,
      ...newDimensions
    }));

    // Update selected format with new aspect ratio and dimensions
    setSelectedFormat(prev => ({
      ...prev,
      aspectRatio: newIsPortrait ? height / width : width / height,
      currentDimensions: newDimensions
    }));
  };

  const handleFromDateChange = (date) => {
    setFromDate(date);
    setFilters(prev => ({ ...prev, startDate: date }));
  };

  const handleToDateChange = (date) => {
    setToDate(date);
    setFilters(prev => ({ ...prev, enddate: date }));
  };

  const handleAllDatesChange = (checked) => {
    setAllDates(checked);
    // if (checked) {
    //   setFilters(prev => ({ ...prev, startDate: null, endDate: null }));
    // }
  };

  const handleCommuteChanged = (checked) => {
    setCommutes(checked);
    // setFilters(prev => ({ ...prev, showCommute: checked }));
    // updateFilteredActivities();
  };

  const handleActivitySelectionChange = (selectedActivities) => {
    if (activitySource === 'strava') {
      // Handle Strava activities
      if (stravaStatus.activities) {
        const newEnabledActivities = Object.keys(stravaStatus.activities).reduce((acc, activity) => {
          acc[activity] = selectedActivities.includes(activity);
          return acc;
        }, {});
        // setEnabledActivities(newEnabledActivities);
        setFilteredActivities(newEnabledActivities);
      }
    } else if (activitySource === 'garmin') {
      // Handle Garmin activities
      if (garminStatus.activities) {
        const newEnabledActivities = Object.keys(garminStatus.activities).reduce((acc, activity) => {
          acc[activity] = selectedActivities.includes(activity);
          return acc;
        }, {});
        setGarminActivities(newEnabledActivities);
      }
    } else if (activitySource === 'manual') {
      // Handle manual activities
      // For manual activities, selectedActivities is directly the new state
      setEnabledActivities(selectedActivities);
    }
    // updateFilteredActivities();
  };

  // useEffect(() => {
  //   if (activitySource === 'strava' && stravaStatus.activities) {
  //     const initialEnabledActivities = Object.keys(stravaStatus.activities).reduce((acc, type) => {
  //       acc[type] = true;
  //       return acc;
  //     }, {});
  //     setEnabledActivities(initialEnabledActivities);
  //   } else if (activitySource === 'manual' && manualActivities.length > 0) {
  //     console.log("INIT");
  //     const initialEnabledActivities = manualActivities.reduce((acc, activity) => {
  //       acc[activity._id] = true;
  //       return acc;
  //     }, {});
  //     setEnabledActivities(initialEnabledActivities);
  //   }
  // }, [stravaStatus.activities, manualActivities, activitySource]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const parseDimensions = (dimensionStr) => {
    const [width, height] = dimensionStr.split('x').map(d => parseFloat(d.replace(',', '.')));
    return { width: Number(width), height: Number(height) };
  };

  const handleSettingsChange = (setting, value) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [setting]: value,
    }));
  };


  const handleFrameSettingsChange = (newSettings) => {
    setFrameSettings(newSettings);
  };


  useEffect(() => {
    if (activeMapSection !== 'frame' || !addFrame) return;

    if (window.frameTimeout) {
      clearTimeout(window.frameTimeout);
    }

    setIsLoadingFrame(true);

    window.frameTimeout = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/generate_frame_preview`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(frameSettings),
        });
        const data = await response.json();
        setFrameImage(`data:image/png;base64,${data.image}`);
      } catch (error) {
        toast({
          title: "Error",
          description: "An error occured generating the frame preview",
          variant: "destructive",
          duration: 3000,
        });
      } finally {
        setIsLoadingFrame(false);
      }
    }, 1000);

    return () => {
      if (window.frameTimeout) {
        clearTimeout(window.frameTimeout);
      }
    };
  }, [addFrame, frameSettings]);

  const handleColorSettingChange = (category, setting, value) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [category]: {
        ...prevSettings[category],
        [setting]: value,
      },
    }));
  };

  const handleActivityTypeSettingChange = (activityType, setting, value) => {
    setSettings(prevSettings => {
      const prevByType = prevSettings.activities?.byType || {};
      // `__reset__` clears this type's override entirely so it falls back to
      // the common activity color/alpha/lineWidth.
      if (setting === '__reset__') {
        const { [activityType]: _removed, ...rest } = prevByType;
        return {
          ...prevSettings,
          activities: { ...prevSettings.activities, byType: rest },
        };
      }
      return {
        ...prevSettings,
        activities: {
          ...prevSettings.activities,
          byType: {
            ...prevByType,
            [activityType]: {
              ...(prevByType[activityType] || {}),
              [setting]: value,
            },
          },
        },
      };
    });
  };

  const handleCloseEnlarged = () => {
    setEnlargedImage(null);
  };

  const handlekm2change = async (e) => {
    const km2 = e.target.value === '' ? '' : Number(e.target.value);

    handleSettingsChange('km2', km2);

    if (window.km2timeout) {
      clearTimeout(window.km2timeout);
    }

    window.km2timeout = setTimeout(() => {
      const finalKm2 = Number(km2) || 1;
      handleSettingsChange('km2', finalKm2);

      if (finalKm2 > 4000 && finalKm2 <= 15000) {
        handleSettingsChange('buildings', { ...settings.buildings, active: false });
        handleSettingsChange('streets', { ...settings.streets, details: 'roads' });
      }
      if (finalKm2 > 15000) {
        handleSettingsChange('buildings', { ...settings.buildings, active: false });
        handleSettingsChange('streets', { ...settings.streets, active: false });
      }
      // if (finalKm2 > 20000) {
      //   handleSettingsChange('forests', { ...settings.forests, active: false });
      //   handleSettingsChange('naturalareas', { ...settings.naturalareas, active: false });
      // }
      // if (finalKm2 > 50000) {
      //   handleSettingsChange('water', { ...settings.water, active: false });
      // } else if (finalKm2 < 1) {
      //   setTopography(false)
      // }
    }, 1500);
    // Intentionally no setShouldZoom: km² changes resize the print-area
    // rectangle but must not alter the map's zoom. The only things that
    // should change zoom are the user (wheel/pinch) or the explicit
    // zoom-to-rectangle button.
  };

  useEffect(() => {
    // if (settings.km2 > 4000) {
    //   handleSettingsChange('buildings', { ...settings.buildings, active: false });
    //   handleSettingsChange('streets', { ...settings.streets, details: 'roads' });
    // }
    // else if (settings.km2 > 8000) {
    //   handleSettingsChange('buildings', { ...settings.buildings, active: false });
    //   handleSettingsChange('streets', { ...settings.streets, active: false });
    // }
    if (settings.km2 > 20000) {
      handleSettingsChange('buildings', { ...settings.buildings, active: false });
      handleSettingsChange('streets', { ...settings.streets, active: false });
    }
    // Above the basic-only threshold, "Fully customizable" mode would have to
    // fetch OSM features for an unreasonable number of cells. Force the user
    // into Basic mode so a basemap-tile render is used instead. We don't
    // auto-flip back when km² drops below the threshold — the user keeps
    // whatever mode they last chose.
    if (settings.km2 > BASIC_ONLY_KM2_THRESHOLD && settings.renderMode !== 'basic') {
      handleSettingsChange('renderMode', 'basic');
    }
    // if (settings.km2 > 20000) {
    //   handleSettingsChange('forests', { ...settings.forests, active: false });
    //   handleSettingsChange('naturalareas', { ...settings.naturalareas, active: false });
    // }
    // if (settings.km2 > 50000) {
    //   handleSettingsChange('water', { ...settings.water, active: false });
    // } else if (settings.km2 < 1) {
    //   setTopography(false)
    // }
  }, [settings.km2]);
  // }, [settings.buildings.active]);

  const handlePlaceNameChange = async (e) => {
    const newPlaceName = e.target.value;
    handleSettingsChange('placeName', newPlaceName);

    // Clear any existing timeout
    if (window.placeNameTimeout) {
      clearTimeout(window.placeNameTimeout);
    }
    if (window.borderCheckTimeout) {
      clearTimeout(window.borderCheckTimeout);
    }

    // Clear results if input is empty
    if (newPlaceName.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    // Set a new timeout to geocode after 500ms of no typing
    window.placeNameTimeout = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(newPlaceName)}&limit=5`
        );
        const data = await response.json();

        if (data && data.length > 0) {
          setSearchResults(data);
          setShowDropdown(true);
        } else {
          setSearchResults([]);
          setShowDropdown(false);
        }
      } catch (error) {
        toast({
          title: "Error",
          description: `An error in using Nominatim for ${newPlaceName}`,
          variant: "destructive",
          duration: 3000,
        });
        setSearchResults([]);
        setShowDropdown(false);
      }
    }, 500);
  };

  const handleResultSelect = (result) => {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);

    if (!isNaN(lat) && !isNaN(lon)) {
      // Update the input with the selected place name. We deliberately do
      // NOT auto-recenter the map: the user controls centering via the
      // explicit "Center" button next to the location pill, so picking a
      // suggestion only updates the stored name.
      handleSettingsChange('placeName', result.display_name);
      trackMapConfigEvents.placeSelected(result.display_name);
      rememberRecentPlace(result.display_name);

      // Hide dropdown
      setShowDropdown(false);
      setSearchResults([]);
    }
  };

  // Walk a Nominatim geojson geometry and yield every [lon, lat] pair on
  // every outer/inner ring. Used to compute an antimeridian-safe bbox.
  const flattenGeoJSONLngLats = (geom) => {
    const out = [];
    if (!geom) return out;
    const eat = (ring) => { for (const [lon, lat] of ring) out.push([lon, lat]); };
    if (geom.type === 'Polygon') {
      for (const ring of geom.coordinates) eat(ring);
    } else if (geom.type === 'MultiPolygon') {
      for (const poly of geom.coordinates) for (const ring of poly) eat(ring);
    }
    return out;
  };

  // Antimeridian-safe bbox from a list of [lon, lat] coordinates. Returns
  // { minLat, maxLat, westLon, eastLon } where eastLon may exceed 180 when
  // the feature crosses the dateline (e.g. New Zealand, Russia). Algorithm:
  // sort longitudes, find the LARGEST circular gap — the bbox is the rest
  // of the longitudes (the smaller arc that covers all points).
  const computeBboxFromCoords = (coords) => {
    if (coords.length === 0) return null;
    let minLat = Infinity, maxLat = -Infinity;
    for (const [, lat] of coords) {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    const lons = coords.map(c => c[0]).sort((a, b) => a - b);
    const n = lons.length;
    let largestGap = -Infinity;
    let gapBeforeIdx = n - 1;  // index of lon JUST BEFORE the largest gap
    for (let i = 0; i < n - 1; i++) {
      const g = lons[i + 1] - lons[i];
      if (g > largestGap) { largestGap = g; gapBeforeIdx = i; }
    }
    const circGap = (lons[0] + 360) - lons[n - 1];
    if (circGap > largestGap) { largestGap = circGap; gapBeforeIdx = n - 1; }
    let westLon = lons[(gapBeforeIdx + 1) % n];
    let eastLon = lons[gapBeforeIdx];
    if (westLon > eastLon) eastLon += 360;  // crosses antimeridian
    return { minLat, maxLat, westLon, eastLon };
  };

  const handleSetPlace = async () => {
    if (settings.placeName.length < 2) return;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&polygon_geojson=1&q=${encodeURIComponent(settings.placeName)}&limit=1`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lon = parseFloat(data[0].lon);
        if (!isNaN(lat) && !isNaN(lon)) {
          setMapCenter([lat, lon]);
          // The previous activity line width was tuned for the previous
          // bbox — applying it to the new place's (different size) bbox
          // looks wrong. Snap back to auto so the renderer derives a
          // sensible width from the new area.
          handleColorSettingChange('activities', 'lineWidthAuto', true);

          // Auto-fit print area + map view to the place's bbox when
          // Nominatim returned one. boundingbox is [minLat, maxLat,
          // minLon, maxLon] as strings. Note: we deliberately do NOT
          // also call setShouldCenter — that triggers PlaceCenterer's
          // 500ms re-geocode + setView which fires AFTER our fitBounds
          // and shifts the map (and rectangle) off the bbox center.
          // Prefer the polygon-derived bbox: Nominatim's plain `boundingbox`
          // is naive in [-180, 180] space, so antimeridian-spanning places
          // (NZ, Russia, Fiji) come back as "almost the whole equator" —
          // 30 000 km wide. computeBboxFromCoords reconstructs the real
          // (short-arc) bbox by finding the largest circular gap in the
          // polygon's longitudes.
          const polyCoords = flattenGeoJSONLngLats(data[0].geojson);
          const polyBBox = polyCoords.length >= 3
            ? computeBboxFromCoords(polyCoords)
            : null;

          let minLat, maxLat, westLon, eastLon;
          if (polyBBox) {
            ({ minLat, maxLat, westLon, eastLon } = polyBBox);
          } else {
            const bb = data[0].boundingbox;
            if (!Array.isArray(bb) || bb.length !== 4) return;
            const [bMinLat, bMaxLat, bMinLon, bMaxLon] = bb.map(Number);
            if (![bMinLat, bMaxLat, bMinLon, bMaxLon].every(Number.isFinite)
              || bMaxLat <= bMinLat || bMaxLon <= bMinLon) return;
            minLat = bMinLat; maxLat = bMaxLat;
            westLon = bMinLon; eastLon = bMaxLon;
          }
          {
            // (kept this block braced so the rest of the original code reads
            // unchanged; the validity guard is now handled above.)
            if (true) {
              // Bbox dimensions in meters (small-bbox-friendly approximation;
              // pulling in turf just for this is overkill).
              const avgLat = (minLat + maxLat) / 2;
              const widthM = (eastLon - westLon) * 111320 * Math.cos(avgLat * Math.PI / 180);
              const heightM = (maxLat - minLat) * 111320;
              const placePortrait = heightM > widthM;

              // Pick the format whose aspect ratio (long/short) is closest
              // to the place's. log-distance is symmetric, so 1.4 vs 0.71
              // get treated equivalently regardless of orientation.
              const longShort = placePortrait ? heightM / widthM : widthM / heightM;
              const formatRatios = {
                '4:3': 4 / 3,
                '3:2': 3 / 2,
                '16:9': 16 / 9,
                'DIN': 29.7 / 21,
                '1:1': 1,
              };
              let bestKey = '4:3';
              let bestDiff = Infinity;
              for (const [key, r] of Object.entries(formatRatios)) {
                const diff = Math.abs(Math.log(longShort / r));
                if (diff < bestDiff) { bestDiff = diff; bestKey = key; }
              }

              // Build the new format object, mirroring the picker at line ~2417.
              const [fw, fh] = FORMATS[bestKey].S
                .split('x').map(d => parseFloat(d.replace(',', '.')));
              const dims = {
                width: fw,
                height: fh,
                originalWidth: placePortrait ? fh : fw,
                originalHeight: placePortrait ? fw : fh,
              };
              const figureDims = placePortrait
                ? { figure_width: dims.height, figure_height: dims.width }
                : { figure_width: dims.width, figure_height: dims.height };
              const aspectRatioForOrient = placePortrait
                ? dims.height / dims.width
                : dims.width / dims.height;

              // Smallest km² such that the chosen-format rectangle (in the
              // chosen orientation) covers the place bbox with a 10% margin.
              // Same formula as the activity autofit at MapComponent.js:444.
              const margin = 1.1;
              const targetW = widthM * margin;
              const targetH = heightM * margin;
              const R = aspectRatioForOrient;
              const areaByWidth = (targetW * targetW) / R;
              const areaByHeight = targetH * targetH * R;
              const km2 = Math.max(1, Math.ceil(Math.max(areaByWidth, areaByHeight) / 1e6));

              if (placePortrait !== isPortrait) setIsPortrait(placePortrait);
              setSelectedFormat({
                value: bestKey,
                size: 'S',
                label: ` ${bestKey}`,
                dimensions: dims,
                aspectRatio: aspectRatioForOrient,
                currentDimensions: figureDims,
              });
              setSettings(prev => ({ ...prev, ...figureDims, km2 }));

              // Compute the rectangle that matches `km2` + chosen aspect
              // centered on the bbox center, and write it explicitly. Without
              // this the print rectangle only updates once EditableRectangle's
              // moveend handler fires after fitBounds — which can race with
              // other listeners (e.g. AutoInvalidateSize, PlaceCenterer) and
              // leave the visible rectangle at the previous size.
              const centerLat = (minLat + maxLat) / 2;
              // centerLon is in unwrapped space (may exceed 180 for
              // antimeridian-crossing places). Leaflet handles >180 longitudes
              // — it just renders the world wrapped accordingly, which is
              // what we want here so NZ shows up centered on the dateline.
              const centerLon = (westLon + eastLon) / 2;
              const explicitWM = Math.sqrt(km2 * 1e6 * R);
              const explicitHM = Math.sqrt(km2 * 1e6 / R);
              const dLat = (explicitHM / 2) / 111320;
              const dLon = (explicitWM / 2)
                / (111320 * Math.cos(centerLat * Math.PI / 180));
              setRectangleBounds([
                [centerLat - dLat, centerLon - dLon],
                [centerLat + dLat, centerLon + dLon],
              ]);

              // Fit the leaflet view to the place bbox. This bypasses
              // shouldCenter (which only re-centers without changing
              // zoom) so the map actually frames the place. Uses the
              // unwrapped lon coords so an antimeridian-crossing place
              // (NZ, Russia) frames correctly across 180°.
              const placeBounds = [[minLat, westLon], [maxLat, eastLon]];
              setFitToBounds(placeBounds);
              setFitToBoundsNonce(n => n + 1);
              // Yellow boundary flash for 5s, mirroring the activity
              // highlight pattern (HIGHLIGHT_COLOR / 5000ms in
              // MapComponent.js). Prefer the place's actual polygon
              // (Nominatim's `geojson` field, populated by polygon_geojson=1
              // — Munich's city outline, NZ's coastline). Falls back to the
              // bbox rectangle for queries that only return a point.
              const geom = data[0].geojson;
              const isUsablePolygon = geom && (
                geom.type === 'Polygon' || geom.type === 'MultiPolygon'
              );
              if (isUsablePolygon) {
                setPlaceHighlightGeoJSON(geom);
              } else {
                // Build a Polygon from the bbox corners, CCW, closed.
                // Uses unwrapped lon (eastLon may exceed 180 for places
                // straddling the antimeridian — leaflet handles that).
                setPlaceHighlightGeoJSON({
                  type: 'Polygon',
                  coordinates: [[
                    [westLon, minLat],
                    [eastLon, minLat],
                    [eastLon, maxLat],
                    [westLon, maxLat],
                    [westLon, minLat],
                  ]],
                });
              }
              setPlaceHighlightNonce(n => n + 1);
            }
          }
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `An error in using Nominatim for ${settings.placeName}`,
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  // The active style tab is the source of truth for what gets rendered.
  // Persisted settings.renderMode is mutated by the km²-watcher and can
  // drift out of sync with the tab the user is actually viewing, so we
  // resolve it freshly at every generate call. The km² safety cap is
  // preserved so an over-threshold area can never request a feature render
  // (which would be unreasonably expensive on the backend).
  const resolveSettingsForGenerate = (extra = {}) => {
    const overTooLarge = settings.km2 > BASIC_ONLY_KM2_THRESHOLD;
    const renderMode = (styleTabView === 'basic' || overTooLarge) ? 'basic' : 'features';
    return {
      ...settings,
      ...extra,
      renderMode,
      selectedStyle: settings.selectedStyle || DEFAULT_BASIC_STYLE_ID,
    };
  };

  const generateFinalPlot = async () => {
    setIsGeneratingFinal(true);
    setFinalImage(null);
    // Seed the progress UI immediately so the bar shows up before the
    // first poll lands. The backend overwrites this on its first checkpoint.
    setGenerationStatus({ state: 'running', label: 'Starting…', progress: 0 });

    const generationStartTime = Date.now();
    try {
      // Reset dimensions to S size
      const sDimensions = selectedFormat.value === 'custom'
        ? selectedFormat.dimensions // Custom format already stores dimensions
        : parseDimensions(FORMATS[selectedFormat.value].S);

      setSettings(prev => ({
        ...prev,
        figure_width: sDimensions.width,
        figure_height: sDimensions.height
      }));

      const finalDimensions = isPortrait
        ? { figure_width: sDimensions.height, figure_height: sDimensions.width }
        : { figure_width: sDimensions.width, figure_height: sDimensions.height };

      // Reuse the cached max_dpis if the new rectangle is either the exact
      // same one or "similar" (close center + similar km²) to the last one
      // we generated against — both yield the same DPI caps in practice, so
      // skipping the QGIS calibration probe is safe.
      const rectangleBoundsAreEqual = lastGeneratedSettings?.rectangleBounds &&
        JSON.stringify(lastGeneratedSettings.rectangleBounds) === JSON.stringify(rectangleBounds);
      const boundsReusable = rectangleBoundsAreEqual || rectangleBoundsAreSimilar(
        lastGeneratedSettings?.rectangleBounds,
        rectangleBounds,
        lastGeneratedSettings?.settings?.km2,
        settings?.km2,
      );
      const shouldIncludeMaxDpis = boundsReusable && maxDpis;

      const settingsForGenerate = resolveSettingsForGenerate(finalDimensions);

      setLastGeneratedSettings({
        settings: settingsForGenerate,
        selectedFormat,
        isPortrait,
        mapCenter,
        rectangleBounds,
        filteredActivities: effectiveActivityFilter,
        hiddenActivityIds,
        commutes,
        allDates,
        dateFrom: allDates ? null : dateBoundaryISO(fromDate, 'start'),
        dateTo: allDates ? null : dateBoundaryISO(toDate, 'end'),
        topography,
        frame: FRAME_ENABLED ? addFrame : false,
        frameSettings,
        activitySource,
      });

      await saveToBackend();

      const requestData = {
        settings: settingsForGenerate,
        filteredActivities: effectiveActivityFilter,
        hiddenActivityIds,
        includeCommutes: commutes,
        selectedFormat,
        rectangleBounds,
        allDates,
        dateFrom: allDates ? null : dateBoundaryISO(fromDate, 'start'),
        dateTo: allDates ? null : dateBoundaryISO(toDate, 'end'),
        topography,
        frame: FRAME_ENABLED ? addFrame : false,
        frameSettings,
        activitySource: activitySource === "none"
          ? "none"
          : activitySource === "strava"
            ? (stravaStatus.activities ? "strava" : "none")
            : activitySource === "garmin"
              ? (garminStatus.activities ? "garmin" : "none")
              : (manualActivities.length > 0 ? "manual" : "none")
      };

      if (shouldIncludeMaxDpis) {
        requestData.maxDpis = maxDpis;
      }
      const response = await fetch(`${API_URL}/generate_final`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });
      const generationTimeSeconds = (Date.now() - generationStartTime) / 1000;

      if (response.status === 409) {
        // Another tab/request already holds the slot — don't show an error;
        // the polling loop will mirror the in-flight generation's progress
        // and pick up its result when done.
        return;
      }

      if (response.ok) {
        trackGenerationEvents.generateSuccess(generationTimeSeconds, requestData);
        // Cache the per-size DPI table so the next regeneration with the
        // same rectangle skips the QGIS calibration probe entirely
        // (shouldIncludeMaxDpis above honors this on the next request).
        const maxDpisHeader = response.headers.get('X-Max-Dpis');
        if (maxDpisHeader) {
          try {
            setMaxDpis(JSON.parse(maxDpisHeader));
          } catch (_e) { /* ignore malformed header */ }
        }
        const blob = await response.blob();
        setFinalImage(URL.createObjectURL(blob));
        setIsUnlocked(false);
        toast({
          title: "Map generated successfully",
          description: "Your map has been generated and is ready to view in the preview section.",
          duration: 7000,
          action: (
            <div
              className="w-full h-full absolute inset-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveMapSection('preview');
              }}
            />
          ),
        });

      } else if (response.status === 499) {
        // User-initiated abort — backend signals it via 499 (nginx's
        // "Client Closed Request"). Surface it as a neutral toast, not
        // a destructive Error, and don't fire generateError tracking.
        toast({
          title: "Generation aborted",
          description: "Cells already fetched are cached for next time.",
          duration: 5000,
        });
      } else {
        trackGenerationEvents.generateError(generationTimeSeconds, requestData);
        toast({
          title: "Error",
          description: 'Failed to generate your map. Please try again.',
          variant: "destructive",
          duration: 7000,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while generating your map!",
        variant: "destructive",
        duration: 7000,
      });
    } finally {
      setIsGeneratingFinal(false);
    }
  };

  const setMaxDpisLastMap = async (maxDpis, bounds) => {
    setMaxDpis(maxDpis);
  }

  // Recent-maps fetch lives at the dashboard level so the result survives
  // section toggles. MobileMapLayout (preview section) reads it via props.
  const fetchRecentMaps = React.useCallback(async () => {
    setIsLoadingRecentMaps(true);
    setRecentMapsError(null);
    try {
      const response = await fetch(`${API_URL}/recent_maps`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch recent maps');
      const data = await response.json();
      // Backend now ships imageUrl as a relative `/api/...?token=` path so
      // the browser can hit the new thumbnail endpoint with cache support.
      // In dev (REACT_APP_API_URL=http://localhost:5500) we have to prepend
      // the host explicitly — `<img src="/api/...">` would otherwise resolve
      // to the CRA dev server, which doesn't know that route.
      const apiHost = process.env.REACT_APP_API_URL || '';
      if (apiHost && Array.isArray(data?.maps)) {
        data.maps = data.maps.map((m) => (
          m && typeof m.imageUrl === 'string' && m.imageUrl.startsWith('/api/')
            ? { ...m, imageUrl: `${apiHost}${m.imageUrl}` }
            : m
        ));
      }
      setRecentMapsData(data || { maps: [] });
      // Keep the M-size capability for the post-generate purchase flow in
      // sync with whatever the most recent map actually supports.
      if (data?.maps?.length > 0 && data.maps[0].max_dpis) {
        setMaxDpisLastMap(data.maps[0].max_dpis);
      }
    } catch (err) {
      console.error('Error fetching recent maps:', err);
      setRecentMapsError(err.message);
    } finally {
      setIsLoadingRecentMaps(false);
      setRecentMapsFetched(true);
    }
  }, [API_URL]);

  // Used to be deferred until the Preview tab opened (the response was
  // ~4 MB of base64 thumbnails). Now /recent_maps is small metadata + URLs,
  // so prefetch on dashboard mount — the user almost always ends up on
  // Preview eventually, and warming the cache here means the section is
  // already populated when they get there.
  useEffect(() => {
    if (!recentMapsFetched && !isLoadingRecentMaps) {
      fetchRecentMaps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh after a successful generation so the new map appears in the row.
  useEffect(() => {
    if (finalImage && recentMapsFetched) fetchRecentMaps();
  }, [finalImage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Single source of truth for "is a generation in flight?". Combines the
  // in-flight POST (isGeneratingFinal) and the persistent server-side state
  // (generationStatus) — either alone is enough to disable the button and
  // show the progress bar.
  const isGenerationActive =
    isGeneratingFinal || generationStatus?.state === 'running';


  // Reset the local "Aborting…" indicator once the server confirms the
  // generation is no longer running (state changes to error/done/idle).
  useEffect(() => {
    if (isAborting && generationStatus?.state !== 'running') {
      setIsAborting(false);
    }
  }, [isAborting, generationStatus?.state]);

  const abortGeneration = async () => {
    if (!isGenerationActive || isAborting) return;
    setIsAborting(true);
    try {
      const r = await fetch(`${API_URL}/user/generation/abort`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) {
        // Hide the noise — backend logs the actual error and the next
        // status poll will clear isAborting either way.
        console.error('abort request failed', r.status);
      }
    } catch (e) {
      console.error('abort request error', e);
    }
  };

  // Block Generate / Tipps clicks while the first recent_maps fetch is in
  // flight: Tipps' "Show me" highlights the Compare button which lives inside
  // the not-yet-rendered RecentMaps, and we don't want a generate to land
  // before we know which max_dpis applies to the latest map.
  const previewLoadBlocking = isLoadingRecentMaps && !recentMapsFetched;

  // On mount, ask the backend whether this account already has a generation
  // running (e.g. from another tab or a pre-reload session). If so, take
  // over with progress polling.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/user/generation-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled && data && data.state) {
          setGenerationStatus(data);
        }
      } catch (_e) { /* offline, no-op */ }
    })();
    return () => { cancelled = true; };
    // Run once per fresh mount; the polling effect below handles updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while a generation is running. Stops as soon as state flips out of
  // 'running' (done / error / idle). 2s cadence is a fair compromise between
  // UI responsiveness and Mongo load.
  useEffect(() => {
    if (generationStatus?.state !== 'running') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API_URL}/user/generation-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (!cancelled) setGenerationStatus(data);
      } catch (_e) { /* transient network — keep polling */ }
    };
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [generationStatus?.state]);

  // When a generation completes (we weren't the tab that fired it, OR we
  // reloaded mid-run), the response blob never reached this tab. Recover by
  // refetching the recent maps so the latest one becomes the displayed
  // image. We only do this when finalImage is empty so a freshly-generated
  // image from THIS tab's POST isn't clobbered.
  useEffect(() => {
    if (generationStatus?.state !== 'done') return;
    if (finalImage) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/recent_maps`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        const latest = (data?.maps || [])[0];
        if (!latest) return;
        if (latest.max_dpis) setMaxDpis(latest.max_dpis);
        // The /recent_maps payload exposes a watermark URL the dashboard
        // can display directly without a second blob fetch. Rewrite the
        // relative path to the configured API host (only matters in dev).
        const apiHost = process.env.REACT_APP_API_URL || '';
        const url = (apiHost && typeof latest.imageUrl === 'string' && latest.imageUrl.startsWith('/api/'))
          ? `${apiHost}${latest.imageUrl}`
          : latest.imageUrl;
        if (url) setFinalImage(url);
      } catch (_e) { /* swallow — next generation will refresh */ }
    })();
  }, [generationStatus?.state, finalImage]);

  const handleSizeSelect = async (imageId, size, dimensions, needsRegeneration) => {
    setIsUnlocking(true);
    try {
      if (!imageId) {
        toast({
          title: "Error",
          description: "No image to unlock",
          variant: "destructive",
        });
        return;
      }
      let height = dimensions.height;
      let width = dimensions.width;
      if (size === "custom") {
        height = dimensions.originalHeight;
        width = dimensions.originalWidth;
      }
      trackPurchaseEvents.checkoutInitiated(size, width, width);
      // Create checkout session first
      const checkoutResponse = await fetch(`${API_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageId: imageId,
          size: size,
          height: height,
          width: width,
          needsRegeneration,
          voucher: voucher
        }),
      });

      if (!checkoutResponse.ok) {
        trackPurchaseEvents.checkoutFailed();
        throw new Error('Failed to create checkout session');
      }
      localStorage.setItem('currentImageId', imageId);
      const { checkoutUrl } = await checkoutResponse.json();

      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;

    } catch (error) {
      trackPurchaseEvents.checkoutFailed();
      toast({
        title: "Error",
        description: "An error occured",
        variant: "destructive",
        duration: 3000,
      });
      setIsUnlocking(false);
    }
  };

  // Print orders ride their own endpoint because they need a shipping
  // address (Stripe collects it). Voucher is shared with the digital
  // flow — same code applies to either kind of purchase.
  const handlePrintSelect = async (imageId, { size, dimensions, frame, frameColor, passepartoutCm, voucher: printVoucher, shipping, email }) => {
    setIsUnlocking(true);
    try {
      if (!imageId) {
        toast({
          title: "Error",
          description: "No image to order",
          variant: "destructive",
        });
        return;
      }
      const resp = await fetch(`${API_URL}/create-print-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageId,
          size,
          frame,
          frameColor,
          passepartoutCm,
          voucher: printVoucher || voucher,
          height: dimensions.height,
          width: dimensions.width,
          // Only sent for free-tier orders, where Stripe can't collect
          // the address itself. Backend ignores these for paid orders.
          shipping,
          email,
        }),
      });
      if (!resp.ok) throw new Error('Failed to create print checkout');
      localStorage.setItem('currentImageId', imageId);
      const { checkoutUrl } = await resp.json();
      window.location.href = checkoutUrl;
    } catch (error) {
      toast({
        title: "Error",
        description: "Could not start print checkout",
        variant: "destructive",
        duration: 3000,
      });
      setIsUnlocking(false);
    }
  };

  const haveSettingsChanged = () => {
    if (!lastGeneratedSettings) return true;

    // Deep comparison of relevant settings
    const relevantCurrentSettings = {
      settings,
      selectedFormat,
      isPortrait,//: lastGeneratedSettings.isPortrait, // Use the portrait setting from last generation
      mapCenter,
      rectangleBounds,
      filteredActivities: effectiveActivityFilter,
      hiddenActivityIds,
      commutes,
      allDates,
      dateFrom: allDates ? null : dateBoundaryISO(fromDate, 'start'),
      dateTo: allDates ? null : dateBoundaryISO(toDate, 'end'),
      topography,
      frame: FRAME_ENABLED ? addFrame : false,
      frameSettings,
      activitySource
    };
    relevantCurrentSettings.settings.figure_width = lastGeneratedSettings.settings.figure_width;
    relevantCurrentSettings.settings.figure_height = lastGeneratedSettings.settings.figure_height;

    const relevantLastSettings = {
      ...lastGeneratedSettings
    };
    return JSON.stringify(relevantCurrentSettings) !== JSON.stringify(relevantLastSettings);
  };

  const handleResetToPlace = () => {
    handlePlaceNameChange({ target: { value: settings.placeName } });
  }

  const FormatCard = ({ format, isSelected, onSelect }) => {
    return (
      <Card
        className={`cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
        onClick={() => onSelect(format.value)}
      >
        <CardContent className="flex mt-2 items-center justify-center h-full p-0">
          <span className="text-sm font-medium text-center">{format.label}</span>
        </CardContent>
      </Card>
    );
  };

  const handleDeleteManualActivity = async (activityId) => {
    try {
      const response = await fetch(`${API_URL}/delete_gpx/${activityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        trackActivityEvents.gpxDelete();
        setManualActivities(prev => prev.filter(activity => activity._id !== activityId));
        setEnabledActivities(prev => {
          const newState = { ...prev };
          delete newState[activityId];
          return newState;
        });
      }
    } catch (error) {
      trackActivityEvents.gpxDeleteError();
      toast({
        title: "Error",
        description: "An error occured while deleting a GPX",
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  const SettingSection = ({ title, children }) => (
    <div className="flex flex-col">
      <span className="text-sm text-gray-500 mb-1">{title}</span>
      <div className="text-sm font-medium">{children}</div>
    </div>
  );

  const StatusIcon = ({ active }) => (
    active ?
      <Check className="h-4 w-4 text-green-500 inline-block" /> :
      <X className="h-4 w-4 text-red-500 inline-block" />
  );

  const cn = isPortrait
    ? "grid grid-cols-1 gap-4 mb-6"
    : "grid grid-cols-2 md:grid-cols-4 gap-4 mb-6";

  return (
    // Desktop fills the viewport (h-full chains down to make the
    // gradient background extend to the bottom). Mobile is content-
    // driven so the footer hugs the actual content end — otherwise
    // h-full propagates through Tabs / TabsContent and leaves a tall
    // whitespace band between the map and the footer when the map
    // takes only 55 dvh.
    <div className="flex flex-col lg:h-full">
      {/* Style-apply mode-mismatch prompt. Shown when the user picks a
          predefined / shared style whose activity mode differs from
          the current mode (heatmap ↔ classic lines). The user chooses
          whether to keep their current rendering or adopt the style's. */}
      {/* Confirm prompt for the side-by-side Classic-lines / Heatmap
          toggle in the styles legend. Plain confirm — no settings
          merging — just flip activities.mode when the user accepts. */}
      <Dialog
        open={!!pendingModeFlip}
        onOpenChange={(open) => { if (!open) setPendingModeFlip(null); }}
      >
        <DialogContent className="max-w-md p-6">
          <DialogHeader>
            <DialogTitle>
              Switch to {pendingModeFlip === 'heatmap' ? 'heatmap' : 'classic lines'}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700 mt-2">
            {pendingModeFlip === 'heatmap'
              ? 'All activities will be merged into a frequency heatmap. Per-type colors and line widths disabled in this mode.'
              : 'Each activity will be drawn as an individual line.'}
          </p>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => setPendingModeFlip(null)}>
              Cancel
            </Button>
            <Button
              variant="black"
              onClick={() => {
                const target = pendingModeFlip;
                setPendingModeFlip(null);
                handleColorSettingChange('activities', 'mode', target);
              }}
            >
              Switch
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!pendingStyleApply}
        onOpenChange={(open) => { if (!open) setPendingStyleApply(null); }}
      >
        <DialogContent className="max-w-md p-6">
          <DialogHeader>
            <DialogTitle>Switch activity rendering?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-700 mt-2">
            This style draws activities as{' '}
            <span className="font-semibold">
              {pendingStyleApply?.styleMode === 'heatmap' ? 'a heatmap' : 'classic lines'}
            </span>
            . You're currently using{' '}
            <span className="font-semibold">
              {pendingStyleApply?.currentMode === 'heatmap' ? 'a heatmap' : 'classic lines'}
            </span>
            .
          </p>
          <p className="text-sm text-gray-600 mt-2">
            Keep your current mode, or use the style's?
          </p>
          <div className="flex gap-2 justify-end mt-4">
            <Button
              variant="outline"
              onClick={() => {
                pendingStyleApply?.applyStyle();
                setPendingStyleApply(null);
              }}
              className="rounded-2xl"
            >
              Use style's {pendingStyleApply?.styleMode === 'heatmap' ? 'heatmap' : 'lines'}
            </Button>
            <Button
              variant="black"
              onClick={() => {
                pendingStyleApply?.applyKeep();
                setPendingStyleApply(null);
              }}
            >
              Keep {pendingStyleApply?.currentMode === 'heatmap' ? 'heatmap' : 'lines'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="flex-1 container mx-auto px-2 lg:px-4 lg:py-6 lg:h-full">
        <Tabs value={selectedTab} onValueChange={handleTabChange} className="flex flex-col lg:h-full">
          {/* Tab navigation has moved to the top header (App.js) — the
              TabsList here is intentionally omitted. Create is the default
              view; header buttons switch to history / admin. */}

          {/* Desktop fills the viewport (flex-1 + h-full so the
              gradient panel runs the full height). Mobile is content-
              driven so the footer hugs the bottom of the actual
              content — otherwise the chain of flex-1/h-full forces
              the tab body to fill the screen even when the map only
              uses 55 dvh, leaving a tall whitespace band between the
              map and the footer. */}
          <TabsContent value="create" className="lg:flex-1 lg:h-full">
            <div className="flex flex-col lg:min-h-full">
              <div className="mb-1 sticky top-0 z-[1500] bg-white/95 backdrop-blur-sm -mx-2 lg:-mx-4 px-2 lg:px-4 pt-2 pb-2 shadow-sm">
                <div className="flex items-center space-x-2">
                  <Button
                    onClick={() => {
                      const currentIndex = mapSections.findIndex(section => section.id === activeMapSection);
                      if (currentIndex > 0) {
                        handleSectionChange(mapSections[currentIndex - 1].id);
                      }
                    }}
                    disabled={activeMapSection === mapSections[0].id}
                    // className="p-2 bg-blue-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    className="bg-gray-900 border-0 text-white hover:bg-gray-600 rounded-2xl"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex-1 border-0 bg-gray-100 rounded-2xl p-2">
                    {/* 3-column flex row: Tipps chip on the left, the
                        section tabs centered in the middle, an invisible
                        spacer of matching width on the right so the
                        tabs stay visually centered. Saves the row of
                        vertical space the centered Tipps button used to
                        take below the section bar. */}
                    {(() => {
                      const tippsDisabled = activeMapSection === 'preview' && previewLoadBlocking;
                      const tippsClasses = `flex items-center gap-1 lg:gap-1.5 text-xs lg:text-sm px-2 lg:px-3 py-1 lg:py-1.5 rounded-full lg:rounded-2xl border transition-colors ${tippsDisabled
                        ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                        : 'text-amber-900 bg-amber-100 border-amber-300 hover:bg-amber-200'
                        }`;
                      const TippsInner = (
                        <>
                          <Lightbulb className="h-4 w-4 text-amber-600" />
                          <span className="hidden lg:inline">Tipps for this section</span>
                        </>
                      );
                      return (
                        <div className="flex items-center w-full">
                          <div className="flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => setTippsOpen(true)}
                              disabled={tippsDisabled}
                              title="Tipps for this section"
                              className={tippsClasses}
                            >
                              {TippsInner}
                            </button>
                          </div>
                          <div className="flex-1 flex justify-center min-w-0">
                            <div className="flex flex-wrap gap-0 lg:gap-2 items-center">
                              {mapSections.map((section, index) => (
                                <button
                                  key={section.id}
                                  onClick={() => handleSectionChange(section.id)}
                                  className={`flex items-center min-w-fit px-1 lg:px-3 py-0.5 lg:py-1.5 rounded-2xl transition-colors
                         ${activeMapSection === section.id ? 'bg-black text-white' : 'hover:bg-gray-200'}`}
                                >
                                  <div className="flex items-center gap-1.5 lg:gap-2.5">
                                    <span className="flex items-center justify-center w-4 h-4 lg:w-6 lg:h-6 text-md lg:text-lg rounded-full bg-white/20">
                                      {index + 1}
                                    </span>
                                    <span>{section.icon}</span>
                                    <span className="text-sm hidden sm:inline">{section.label}</span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                          {/* Invisible mirror of the Tipps chip so the
                              centered tabs stay visually centered no
                              matter the chip's width (icon-only on
                              mobile, icon+text on desktop). */}
                          <div className="flex-shrink-0 invisible" aria-hidden="true">
                            <button type="button" className={tippsClasses}>
                              {TippsInner}
                            </button>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  <Button
                    onClick={() => {
                      const currentIndex = mapSections.findIndex(section => section.id === activeMapSection);
                      if (currentIndex < mapSections.length - 1) {
                        handleSectionChange(mapSections[currentIndex + 1].id);
                      }
                    }}
                    disabled={activeMapSection === mapSections[mapSections.length - 1].id}
                    // className="p-2 bg-blue-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    className="bg-gray-900 border-0 text-white hover:bg-gray-600 rounded-2xl"
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* <div className="flex-1 bg-orange-100/80 rounded-xl"> */}
              <div
                className="flex rounded-2xl shadow-xl"
                style={{
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.02), rgba(0,0,60,0.15))'
                }}
              >
                {activeMapSection === 'style' && (() => {
                  // Render-mode tabs sit at this layout level (not inside
                  // the popup) so the whole left+right pair swaps when the
                  // user switches modes:
                  //   - Features: StaticImageSlideshow + ColorLegend
                  //   - Basic:    activities card + BasicStylesGrid + StylePreview
                  // Above 50,000 km² the Features tab is still clickable
                  // (so users can see what they're missing), but its content
                  // is covered by a blurry overlay that explains the limit
                  // and offers to switch to Basic.
                  const km2Val = Number(settings?.km2 || 0);
                  const featuresDisabled = km2Val > BASIC_ONLY_KM2_THRESHOLD;
                  // What's currently *visible* — driven by user clicks.
                  // settings.renderMode (what the backend will use) is kept
                  // in sync via handleSettingsChange below + the existing
                  // km² watcher useEffect (which forces 'basic' for too-
                  // large areas regardless of what tab the user is viewing).
                  const tabView = styleTabView;
                  const switchRenderMode = (mode) => {
                    setStyleTabView(mode);
                    // Always update the persisted choice when the user
                    // explicitly picks a tab; the km² watcher will override
                    // it back to 'basic' if it has to.
                    handleSettingsChange('renderMode', mode);
                    setCustomizeOpen(false);
                    setExpandedCategory(null);
                  };
                  const selectedStyleId = settings?.selectedStyle || DEFAULT_BASIC_STYLE_ID;
                  const selectedStyle = getBasicStyle(selectedStyleId);
                  // Activity-related visibility:
                  // - hasActivities: there's a source the user has chosen
                  //   (Strava / Garmin / manual / synced). When 'none', the
                  //   entire activities settings card is hidden — there's
                  //   nothing to color/opacity-tweak.
                  // - activitiesActive: the activities toggle in the
                  //   settings card itself is off. We still show the card
                  //   (it's the only way to turn it back on), but skip
                  //   plotting polylines on the live preview to match
                  //   what the print will look like.
                  const hasActivities = activitySource && activitySource !== 'none';
                  const activitiesActive = settings?.activities?.active !== false;
                  const previewActivities = (hasActivities && activitiesActive)
                    ? basicPreviewActivities
                    : [];

                  return (
                    <div className="flex flex-col h-full p-6 lg:p-8 w-full gap-3">
                      {/* Tabs (hidden while the customize sheet is open
                          since that view is full-bleed). */}
                      {!customizeOpen && (
                        <div className="flex border-b border-gray-300">
                          <button
                            type="button"
                            onClick={() => switchRenderMode('features')}
                            // Always enabled — the size limit shows up as
                            // an overlay over the content, not as a hard
                            // block on the tab itself, so the user can
                            // see what they'd be using if they shrunk the
                            // area.
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tabView === 'features'
                              ? 'border-gray-900 text-gray-900'
                              : 'border-transparent text-gray-500 hover:text-gray-800'
                              }`}
                          >
                            Fully customizable
                          </button>
                          <button
                            type="button"
                            onClick={() => switchRenderMode('basic')}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tabView === 'basic'
                              ? 'border-gray-900 text-gray-900'
                              : 'border-transparent text-gray-500 hover:text-gray-800'
                              }`}
                          >
                            Basic
                          </button>
                        </div>
                      )}

                      {/* Basic-tab view uses a 2:3 split (smaller left,
                          bigger live preview); Features uses 3:1 (slideshow
                          + small legend). Customize sheet is full-bleed. */}
                      <div className={`grid grid-cols-1 ${customizeOpen
                        ? ''
                        : tabView === 'basic'
                          ? 'lg:grid-cols-5'
                          : 'lg:grid-cols-4'} gap-4 lg:gap-6 flex-1`}>
                        <div className={customizeOpen
                          ? 'min-w-0 w-full max-w-4xl mx-auto'
                          : `min-w-0 ${tabView === 'basic' ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
                          {customizeOpen ? (
                            <div className="flex flex-col">
                              <div className="flex items-center justify-center mb-3">
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    if (expandedCategory) {
                                      setExpandedCategory(null);
                                    } else {
                                      setCustomizeOpen(false);
                                    }
                                  }}
                                  className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-gray-800 border-gray-300 hover:bg-gray-100 rounded-2xl shadow-sm"
                                >
                                  <ArrowLeft className="h-5 w-5" />
                                  Back
                                </Button>
                              </div>
                              <div className="md:max-h-[calc(3*10rem)] lg:max-h-[80vh] md:overflow-y-auto pr-1">
                                <ColorSettingsPopup
                                  // Customize sheet is only reachable from the
                                  // Fully Customizable tab, so always show the
                                  // features grid even when the km² watcher
                                  // has flipped settings.renderMode to 'basic'.
                                  settings={{ ...settings, renderMode: 'features' }}
                                  onColorSettingChange={handleColorSettingChange}
                                  onActivityTypeSettingChange={handleActivityTypeSettingChange}
                                  activitySource={activitySource}
                                  triggerButton={false}
                                  syncOceansWater={syncWaterOceans}
                                  onSyncToggle={handleSyncWaterOceans}
                                  onPaperstyleToggle={(v) => handleSettingsChange('paperstyle', v)}
                                  onWatercolorToggle={(v) => handleSettingsChange('watercolor', v)}
                                  selectedCategory={expandedCategory}
                                  onSelectedCategoryChange={setExpandedCategory}
                                  onShowAddActivities={flashNoActivities}
                                />
                              </div>
                            </div>
                          ) : tabView === 'basic' ? (
                            <div className="flex flex-col gap-4">
                              {/* Activities card — only when the user has
                                  picked a source. Without one the card
                                  would show an "enable a source" message
                                  with no other controls; just hide it.
                                  Rendered inline (no Done button — see
                                  popup code). */}
                              {hasActivities && (
                                <ColorSettingsPopup
                                  settings={{ ...settings, renderMode: 'basic' }}
                                  onColorSettingChange={handleColorSettingChange}
                                  onActivityTypeSettingChange={handleActivityTypeSettingChange}
                                  activitySource={activitySource}
                                  triggerButton={false}
                                  syncOceansWater={syncWaterOceans}
                                  onSyncToggle={handleSyncWaterOceans}
                                  selectedCategory="activities"
                                  onSelectedCategoryChange={() => { }}
                                />
                              )}
                              <BasicStylesGrid
                                selectedStyleId={selectedStyleId}
                                onSelect={(id) => handleSettingsChange('selectedStyle', id)}
                                rectangleBounds={rectangleBounds}
                                activities={previewActivities}
                                activitySettings={settings.activities}
                              />
                            </div>
                          ) : (
                            // Features tab; covered by an explanatory
                            // overlay when the bbox is too large to render
                            // OSM features (the user can see the layout
                            // they'd be using and easily switch to Basic).
                            <div className="relative">
                              <div className={featuresDisabled ? 'pointer-events-none select-none' : ''}>
                                <div className="flex flex-col items-center">
                                  <StaticImageSlideshow
                                    onSettingsChange={(newSettings) => {
                                      // Applying a style updates the visual
                                      // recipe (colors, alphas, paperstyle
                                      // flag, etc.) but should NOT flip the
                                      // user's activities-on/off choice. If
                                      // they've turned activities off, a
                                      // style template shouldn't silently
                                      // re-enable them on apply (and vice
                                      // versa).
                                      //
                                      // We also detect activity-mode flips
                                      // (heatmap ↔ classic lines) and defer
                                      // those to a user prompt — see the
                                      // pendingStyleApply state above.
                                      const buildMerged = (modeOverride) => (prevSettings) => {
                                        const merged = { ...prevSettings, ...newSettings };
                                        if (newSettings?.activities && prevSettings?.activities) {
                                          merged.activities = {
                                            ...newSettings.activities,
                                            active: prevSettings.activities.active,
                                          };
                                          if (modeOverride !== undefined) {
                                            merged.activities.mode = modeOverride;
                                          }
                                          // If the user chose to KEEP heatmap
                                          // on a style that wanted classic
                                          // lines, the style's alpha (often
                                          // tuned for a line look like 0.2)
                                          // would leave the heatmap almost
                                          // invisible. Force opacity to 1.0
                                          // in that specific case so the
                                          // heatmap reads correctly out of
                                          // the box.
                                          const styleMode = newSettings?.activities?.mode || 'polylines';
                                          const keptHeatmap = modeOverride === 'heatmap' && styleMode !== 'heatmap';
                                          if (keptHeatmap) {
                                            merged.activities.alpha = 1.0;
                                          }
                                        }
                                        // Post-process flags are part of the
                                        // style identity. A spread leaves the
                                        // previous flag in place when the new
                                        // template doesn't mention it, so a
                                        // user who had paperstyle/watercolor
                                        // on would carry it into every other
                                        // style they pick. Force both flags
                                        // to come from the new style and
                                        // default missing ones to false.
                                        merged.paperstyle = !!newSettings?.paperstyle;
                                        merged.watercolor = !!newSettings?.watercolor;
                                        return merged;
                                      };

                                      const currentMode = settings?.activities?.mode || 'polylines';
                                      const styleMode = newSettings?.activities?.mode || 'polylines';
                                      if (currentMode !== styleMode) {
                                        setPendingStyleApply({
                                          currentMode,
                                          styleMode,
                                          applyKeep: () => setSettings(buildMerged(currentMode)),
                                          applyStyle: () => setSettings(buildMerged(styleMode)),
                                        });
                                        return;
                                      }
                                      setSettings(buildMerged(undefined));
                                    }}
                                  >
                                    <Button
                                      size="sm"
                                      onClick={() => setCustomizeOpen(true)}
                                      className="text-xs lg:text-md rainbow-border-bg text-white hover:bg-gray-600"
                                    >
                                      <SlidersHorizontal className="h-4 w-4 mr-2" />
                                      Customize
                                    </Button>
                                  </StaticImageSlideshow>
                                </div>
                              </div>
                              {featuresDisabled && (
                                <div className="absolute inset-0 backdrop-blur-md bg-white/60 rounded-xl flex items-center justify-center p-6 z-10">
                                  <div className="bg-white rounded-2xl shadow-xl border border-gray-200 max-w-md p-6 text-center space-y-3">
                                    <p className="text-sm font-semibold text-gray-900">
                                      Fully customizable not available at this size
                                    </p>
                                    <p className="text-xs text-gray-600">
                                      Your selected area is {formatKm2(km2Val)}, which is over the {formatKm2(BASIC_ONLY_KM2_THRESHOLD)} limit for per-feature rendering.
                                      Switch to Basic to use a basemap style instead — works at any size.
                                    </p>
                                    <Button
                                      size="sm"
                                      onClick={() => switchRenderMode('basic')}
                                      className="rainbow-border-bg text-white hover:bg-gray-600"
                                    >
                                      Go to Basic styles
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {!customizeOpen && (
                          <div className={`flex flex-col items-center justify-center w-full ${tabView === 'basic' ? 'lg:col-span-3' : 'lg:col-span-1'
                            }`}>
                            {tabView === 'basic' ? (
                              <div className="w-full flex flex-col items-center">
                                <p className="text-sm font-semibold text-gray-700 mb-2">Live preview</p>
                                {/* Match the visual footprint of ColorLegend
                                    so the right column doesn't collapse.
                                    Aspect-[4/3] keeps the preview shaped
                                    like a print and lets it scale with the
                                    column width. */}
                                <div className="w-full rounded-xl overflow-hidden border border-gray-200 shadow-sm aspect-[4/3]">
                                  <StylePreview
                                    style={selectedStyle}
                                    bounds={rectangleBounds}
                                    activities={previewActivities}
                                    activitySettings={settings.activities}
                                  />
                                </div>
                                <p className="mt-2 text-[11px] text-gray-500 text-center">
                                  {selectedStyle.label} — {selectedStyle.sublabel}
                                </p>
                              </div>
                            ) : (
                              <>
                                <p className="text-sm font-semibold text-gray-700">Current settings</p>
                                {/* Activities plot style. Side-by-side
                                    toggle between classic polylines and
                                    a frequency heatmap. Clicking the
                                    inactive one prompts before flipping
                                    so users don't lose their tuning. */}
                                <div className="mt-2">
                                  <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                                    Activities plot style
                                  </p>
                                  {(() => {
                                    const mode = settings.activities?.mode === 'heatmap' ? 'heatmap' : 'polylines';
                                    const click = (target) => {
                                      if (target === mode) return;
                                      setPendingModeFlip(target);
                                    };
                                    return (
                                      <div className="grid grid-cols-2 gap-1">
                                        <button
                                          type="button"
                                          onClick={() => click('polylines')}
                                          className={`flex items-center justify-center gap-1.5 text-xs border rounded-lg px-2 py-1.5 transition-colors ${mode === 'polylines'
                                            ? 'text-gray-900 bg-gray-200 border-gray-400 font-semibold'
                                            : 'text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100'
                                            }`}
                                          aria-pressed={mode === 'polylines'}
                                        >
                                          <span>📍 Classic lines</span>
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => click('heatmap')}
                                          className={`flex items-center justify-center gap-1.5 text-xs border rounded-lg px-2 py-1.5 transition-colors ${mode === 'heatmap'
                                            ? 'text-orange-900 bg-orange-100 border-orange-300 font-semibold'
                                            : 'text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100'
                                            }`}
                                          aria-pressed={mode === 'heatmap'}
                                        >
                                          <span>🔥 Heatmap</span>
                                        </button>
                                      </div>
                                    );
                                  })()}
                                </div>
                                <ColorLegend
                                  settings={settings}
                                  direction="vertical"
                                  onOpenCategory={(category) => {
                                    setExpandedCategory(category);
                                    setCustomizeOpen(true);
                                  }}
                                />
                                {/* Post-process status. Surfaces paperstyle
                                    here so the user sees it without opening
                                    Customize — same place buildings would
                                    appear in the legend, but paperstyle
                                    isn't a per-feature color. */}
                                <div className="mt-3 pt-3 border-t border-gray-100">
                                  <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
                                    Post-process
                                  </p>
                                  {/* Always render both rows so the user
                                      can see which one is on AND which one
                                      is off at the same time. The two
                                      flags are mutually exclusive, so at
                                      most one row shows the "on" state. */}
                                  <div className="space-y-1">
                                    {[
                                      {
                                        key: 'paperstyle',
                                        on: !!settings.paperstyle,
                                        label: '🖌 Hand-painted paper look',
                                        onClasses: 'text-amber-900 bg-amber-100 border-amber-300 hover:bg-amber-200',
                                        onAccent: 'text-amber-700',
                                      },
                                      {
                                        key: 'watercolor',
                                        on: !!settings.watercolor,
                                        label: '🎨 Watercolored',
                                        onClasses: 'text-sky-900 bg-sky-100 border-sky-300 hover:bg-sky-200',
                                        onAccent: 'text-sky-700',
                                      },
                                    ].map(({ key, on, label, onClasses, onAccent }) => (
                                      <button
                                        key={key}
                                        type="button"
                                        onClick={() => setCustomizeOpen(true)}
                                        className={`w-full flex items-center gap-2 text-xs border rounded-lg px-2 py-1.5 transition-colors ${on
                                          ? onClasses
                                          : 'text-gray-600 bg-gray-50 border-gray-200 hover:bg-gray-100'
                                          }`}
                                      >
                                        <span className={on ? 'font-semibold' : ''}>{label}</span>
                                        <span className={`ml-auto ${on ? onAccent : 'text-gray-400'}`}>
                                          {on ? 'on' : 'off'}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
                {activeMapSection === 'map' && (
                  <div className="flex flex-col h-full p-3 lg:p-8 w-full">
                    {/* Grid template varies by state so the right column
                        doesn't leave empty space:
                          - collapsed: single column, map full width
                          - expanded + 'none': [1fr auto] so the bar hugs
                            the map's right edge with no slack
                          - expanded + source: 5-col with 3:2 split */}
                    <div className={`grid grid-cols-1 gap-4 lg:gap-8 w-full lg:items-start ${!activitiesPanelOpen
                      ? 'lg:grid-cols-1'
                      : activitySource === 'none'
                        ? 'lg:grid-cols-[1fr_auto]'
                        : 'lg:grid-cols-5'}`}>
                      {/* Left: top controls + map (spans only above the map) */}
                      <div className={`w-full space-y-3 relative ${activitiesPanelOpen && activitySource !== 'none' ? 'lg:col-span-3' : ''}`}>

                        {settingsLoaded && mapCenter[0] !== null && mapCenter[1] !== null ? (
                          <MapComponent
                            mapCenter={mapCenter}
                            zoom={zoom}
                            onZoomChange={setZoom}
                            restoreViewNonce={restoreViewNonce}
                            rectangleBounds={rectangleBounds}
                            setRectangleBounds={setRectangleBounds}
                            km2={settings.km2}
                            selectedFormat={selectedFormat}
                            placeName={settings.placeName}
                            onMapCenterChange={handleMapCenterChange}
                            shouldCenter={shouldCenter}
                            setShouldCenter={setShouldCenter}
                            shouldZoom={shouldZoom}
                            activitySettings={settings.activities}
                            onActivityLineWidthChange={(v) => handleColorSettingChange('activities', 'lineWidth', v)}
                            onActivityLineWidthAutoChange={(v) => handleColorSettingChange('activities', 'lineWidthAuto', v)}
                            onActivityAlphaChange={(v) => handleColorSettingChange('activities', 'alpha', v)}
                            onActivityModeChange={(v) => handleColorSettingChange('activities', 'mode', v)}
                            showActivityPolylines={settings.activities?.active !== false}
                            activitySources={activitySource === 'none' ? [] : [activitySource]}
                            activityDateFrom={fromDate}
                            activityDateTo={toDate}
                            activityAllDates={allDates}
                            includeCommutes={activitySource !== 'manual' && commutes}
                            mapStyle={mapStyle}
                            onMapStyleChange={setMapStyle}
                            hiddenActivityIds={hiddenActivityIds}
                            activityPolylines={activitiesInView}
                            isLoadingActivities={isLoadingActivities}
                            onMapBoundsChange={fetchActivityPolylines}
                            compact={true}
                            onKm2Change={(v) => handleSettingsChange('km2', v)}
                            onFormatSelect={handleFormatSelect}
                            isPortrait={isPortrait}
                            onTogglePortrait={togglePortrait}
                            onPlaceNameChange={handlePlaceNameChange}
                            searchResults={searchResults}
                            onSelectResult={handleResultSelect}
                            recentPlaces={recentPlaces}
                            onSelectRecent={(name) => {
                              // Recent picks update the stored name only;
                              // user explicitly hits the Center button when
                              // they actually want to move the map.
                              handleSettingsChange('placeName', name);
                              rememberRecentPlace(name);
                            }}
                            onClearPlace={() => {
                              handleSettingsChange('placeName', '');
                              setSearchResults([]);
                            }}
                            onCenterPlace={handleSetPlace}
                            fitToBounds={fitToBounds}
                            fitToBoundsNonce={fitToBoundsNonce}
                            placeHighlightGeoJSON={placeHighlightGeoJSON}
                            placeHighlightNonce={placeHighlightNonce}
                            mapFlashTarget={mapFlashTarget}
                            onOpenActivityPanel={() => setActivitiesPanelOpen(true)}
                          />
                        ) : (
                          <div className="h-[70vh] lg:aspect-[4/3] flex items-center justify-center bg-gray-50 rounded">
                            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                          </div>
                        )}

                        {/* Floating "Plot activities" affordance: sits
                            vertically centered on the map's right edge while
                            the panel is collapsed, so there's always a clear
                            way to reveal the sidebar without leaving the map
                            area. Uses the Strava + Garmin logos as the call
                            to action. */}
                        {!activitiesPanelOpen && (
                          <button
                            type="button"
                            onClick={() => setActivitiesPanelOpen(true)}
                            title="Show activity filters & sync"
                            className={`flex absolute right-1 lg:right-2 top-1/2 -translate-y-1/2 z-[900] items-center gap-1 lg:gap-2 bg-blue-600 hover:bg-blue-700 text-white border border-blue-700 rounded-l-xl shadow-lg pl-2 pr-1 py-2 lg:pl-3 lg:pr-2 lg:py-3 ${plotActivitiesFlashing ? 'animate-tip-flash' : ''}`}
                          >
                            <span className="text-[10px] lg:text-xs font-semibold whitespace-nowrap leading-tight">
                              Plot<br />activities
                            </span>
                            <div className="flex flex-col gap-0.5 lg:gap-1">
                              <img
                                src={StravaPoweredLogo2}
                                alt="Strava"
                                className="h-3.5 lg:h-5 w-auto object-contain bg-white rounded p-0.5"
                              />
                              <img
                                src={GarminLogo}
                                alt="Garmin"
                                className="h-3.5 lg:h-5 w-auto object-contain bg-white rounded p-0.5"
                              />
                            </div>
                          </button>
                        )}
                      </div>

                      {/* Right: activities panel. Collapsed by default so the
                          map takes the full width; the floating button on the
                          map's right edge reveals this panel. Location / size
                          / format controls live inside the map itself. */}
                      {activitiesPanelOpen && (
                        <div className={`w-full flex flex-col gap-2 lg:gap-3 lg:h-full order-first lg:order-none ${activitySource === 'none' ? '' : 'lg:col-span-2'}`}>
                          {/* Hide bar: top-left compact on desktop, full-width
                              horizontal bar at the BOTTOM of the panel on
                              mobile (which sits right above the map, since
                              the panel is order-first on mobile). */}
                          <div className="flex-shrink-0 order-last lg:order-first lg:flex lg:justify-start">
                            <button
                              type="button"
                              onClick={() => setActivitiesPanelOpen(false)}
                              title="Hide panel to enlarge the map"
                              className="w-full lg:w-[74px] rounded-xl border border-gray-300 bg-gray-900 shadow-xl border-0 text-white hover:bg-gray-600 rounded-2xl px-3 py-2 flex flex-row lg:flex-col items-center justify-center gap-1 lg:gap-0.5 leading-tight"
                            >
                              <span className="text-xs font-semibold">Hide</span>
                              <ArrowRight className="h-3.5 w-3.5 rotate-90 lg:rotate-0" />
                            </button>
                          </div>
                          {false && (
                            <div className="border rounded-xl bg-white p-4 space-y-4">
                              <h3 className="text-sm font-semibold">Location</h3>

                              {recentPlaces.length > 0 && (
                                <div className="space-y-2">
                                  <div className="text-xs text-gray-500">Recent</div>
                                  <div className="flex flex-wrap gap-2">
                                    {recentPlaces.map(name => (
                                      <button
                                        key={name}
                                        type="button"
                                        onClick={() => {
                                          handleSettingsChange('placeName', name);
                                          setShouldCenter(true);
                                          rememberRecentPlace(name);
                                        }}
                                        className="px-3 py-1.5 text-xs rounded-full border border-gray-300 bg-white hover:bg-gray-100 truncate max-w-full"
                                        title={name}
                                      >
                                        {name.split(',')[0]}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <div className="relative">
                                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-600 pointer-events-none" />
                                <Input
                                  id="place-name"
                                  type="text"
                                  value={settings.placeName}
                                  onChange={handlePlaceNameChange}
                                  onFocus={() => setShowDropdown(searchResults.length > 0)}
                                  onBlur={() => { setTimeout(() => setShowDropdown(false), 150); }}
                                  className={`w-full h-11 pl-10 border-2 focus:border-blue-500 bg-gray-50 focus:bg-white rounded-xl shadow-sm text-gray-900 placeholder-gray-500 ${settings.placeName ? 'pr-10' : 'pr-3'}`}
                                  placeholder="Search a place, or drag the map"
                                  autoFocus
                                />
                                {settings.placeName && (
                                  <button
                                    type="button"
                                    aria-label="Clear location"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      handleSettingsChange('placeName', '');
                                      setSearchResults([]);
                                      setShowDropdown(false);
                                    }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-200"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                                {showDropdown && searchResults.length > 0 && (
                                  <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                                    {searchResults.map((result, index) => (
                                      <div
                                        key={index}
                                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 text-sm truncate"
                                        title={result.display_name}
                                        onClick={() => handleResultSelect(result)}
                                      >
                                        {result.display_name}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {false && (
                            <div className="border rounded-xl bg-white p-4 space-y-4">
                              <h3 className="text-sm font-semibold">Format</h3>
                              <div className="grid grid-cols-2 gap-2">
                                {Object.keys(FORMATS).map((format) => {
                                  const [w, h] = FORMATS[format].S.split('x').map(d => parseFloat(d.replace(',', '.')));
                                  const display = isPortrait ? `${w}×${h}` : `${h}×${w}`;
                                  const isActive = selectedFormat?.value === format;
                                  return (
                                    <button
                                      key={format}
                                      type="button"
                                      onClick={() => {
                                        const dims = {
                                          width: w,
                                          height: h,
                                          originalWidth: isPortrait ? h : w,
                                          originalHeight: isPortrait ? w : h,
                                        };
                                        handleFormatSelect({
                                          value: format,
                                          size: 'S',
                                          label: ` ${format}`,
                                          dimensions: dims,
                                          aspectRatio: dims.width / dims.height,
                                        });
                                      }}
                                      className={`p-3 border rounded-xl text-center transition-colors ${isActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                                        }`}
                                    >
                                      <div className="font-medium">{format}</div>
                                      <div className="text-xs text-gray-500">{display} cm</div>
                                    </button>
                                  );
                                })}
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Seed sensible defaults and switch the inputs
                                    // visible. Any real ratio is committed via Apply.
                                    setCustomRatioW(customRatioW || '4');
                                    setCustomRatioH(customRatioH || '3');
                                    const w = parseFloat(customRatioW || '4');
                                    const h = parseFloat(customRatioH || '3');
                                    const longest = 30;
                                    const scale = longest / Math.max(w, h);
                                    const sw = w * scale;
                                    const sh = h * scale;
                                    handleFormatSelect({
                                      value: 'custom',
                                      size: 'S',
                                      label: ` ${w}:${h}`,
                                      dimensions: {
                                        width: sw,
                                        height: sh,
                                        originalWidth: isPortrait ? sh : sw,
                                        originalHeight: isPortrait ? sw : sh,
                                      },
                                      aspectRatio: isPortrait ? sh / sw : sw / sh,
                                    });
                                  }}
                                  className={`p-3 border rounded-xl text-center transition-colors ${selectedFormat?.value === 'custom' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                  <div className="font-medium">Custom</div>
                                  <div className="text-xs text-gray-500">
                                    {selectedFormat?.value === 'custom' ? selectedFormat.label : 'W : H'}
                                  </div>
                                </button>
                              </div>

                              {selectedFormat?.value === 'custom' && (
                                <div className="pt-3 border-t space-y-2 flex flex-col items-center">
                                  <div className="text-xs text-gray-600">Custom aspect ratio</div>
                                  <div className="flex items-center gap-2 justify-center">
                                    <Input
                                      type="number"
                                      min="1"
                                      placeholder="W"
                                      value={customRatioW}
                                      onChange={(e) => setCustomRatioW(e.target.value)}
                                      className="w-16 text-center border bg-white rounded-xl"
                                    />
                                    <span className="text-lg font-medium text-gray-500">:</span>
                                    <Input
                                      type="number"
                                      min="1"
                                      placeholder="H"
                                      value={customRatioH}
                                      onChange={(e) => setCustomRatioH(e.target.value)}
                                      className="w-16 text-center border bg-white rounded-xl"
                                    />
                                    <Button
                                      type="button"
                                      onClick={() => {
                                        const w = parseFloat(customRatioW);
                                        const h = parseFloat(customRatioH);
                                        if (!(w > 0 && h > 0)) return;
                                        const longest = 30;
                                        const scale = longest / Math.max(w, h);
                                        const sw = w * scale;
                                        const sh = h * scale;
                                        handleFormatSelect({
                                          value: 'custom',
                                          size: 'S',
                                          label: ` ${w}:${h}`,
                                          dimensions: {
                                            width: sw,
                                            height: sh,
                                            originalWidth: isPortrait ? sh : sw,
                                            originalHeight: isPortrait ? sw : sh,
                                          },
                                          aspectRatio: isPortrait ? sh / sw : sw / sh,
                                        });
                                      }}
                                      disabled={!(parseFloat(customRatioW) > 0 && parseFloat(customRatioH) > 0)}
                                      className="rounded-2xl border-0 bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50"
                                    >
                                      Apply
                                    </Button>
                                  </div>
                                </div>
                              )}

                              <div className="flex items-center gap-3 pt-3 border-t justify-center">
                                <Checkbox
                                  id="portrait-mode"
                                  checked={isPortrait}
                                  onCheckedChange={togglePortrait}
                                  className="h-6 w-6 border-black rounded-xl"
                                />
                                <Label htmlFor="portrait-mode" className="text-sm font-medium cursor-pointer">
                                  Portrait orientation
                                </Label>
                              </div>
                            </div>
                          )}

                          {false && (
                            <div className="border rounded-xl bg-white p-6 space-y-6">
                              <h3 className="text-base font-semibold text-center">Area size</h3>
                              <div className="flex items-baseline gap-2 justify-center">
                                <Input
                                  id="area-size"
                                  type="number"
                                  value={settings.km2}
                                  onChange={handlekm2change}
                                  min={1}
                                  className="w-[140px] text-center text-lg border-2 focus:border-blue-500 bg-gray-50 focus:bg-white rounded-xl"
                                />
                                <span className="text-sm text-gray-600">km²</span>
                              </div>
                              <div className="pt-2 pb-2">
                                <Slider
                                  value={[Math.min(Number(settings.km2) || 1, 20000)]}
                                  min={1}
                                  max={20000}
                                  step={1}
                                  onValueChange={(v) => handleSettingsChange('km2', v[0])}
                                />
                                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                                  <span>1</span>
                                  <span>5 000</span>
                                  <span>10 000</span>
                                  <span>20 000</span>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="text-xs text-gray-500">Quick picks</div>
                                <div className="flex flex-wrap gap-2">
                                  {[10, 50, 100, 500, 1000, 5000, 10000].map(preset => (
                                    <button
                                      key={preset}
                                      type="button"
                                      onClick={() => handleSettingsChange('km2', preset)}
                                      className={`px-3 py-1.5 text-sm rounded-full border ${Number(settings.km2) === preset
                                        ? 'bg-gray-900 text-white border-gray-900'
                                        : 'bg-white border-gray-300 hover:bg-gray-100'
                                        }`}
                                    >
                                      {preset} km²
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {activitiesPanelOpen && (
                            <div className="w-full flex flex-col lg:flex-row gap-0 lg:gap-3 items-stretch flex-1 min-h-0">
                              {/* Source switcher. Vertical on desktop (sits
                                  next to the map with vertical-rl text),
                                  horizontal on mobile (row of icon+label
                                  buttons stacked above the content). */}
                              <div className="inline-flex flex-row lg:flex-col items-stretch gap-1 border border-gray-200 bg-gray-300 rounded-xl p-0 lg:p-1 flex-shrink-0 lg:h-full">
                                <button
                                  type="button"
                                  title="Manual GPX upload"
                                  className={`flex-1 min-h-[36px] lg:w-16 lg:min-h-[44px] px-1 py-1 border-0 rounded-lg flex flex-row lg:flex-col items-center justify-center gap-1 text-xs font-semibold ${activitySource === 'manual' ? 'bg-blue-600 text-white rounded-xl' : 'bg-transparent text-gray-700 hover:bg-gray-200'}`}
                                  onClick={() => handleActivitySourceChange('manual')}
                                >
                                  <Upload className="h-4 w-4" />
                                  <span className="whitespace-nowrap lg:[writing-mode:vertical-lr] lg:rotate-180">GPX</span>
                                </button>
                                <button
                                  type="button"
                                  title="Strava sync"
                                  className={`flex-1 min-h-[36px] lg:w-16 lg:min-h-[44px] px-1 py-1 border-0 rounded-lg flex items-center justify-center overflow-hidden ${activitySource === 'strava' ? 'bg-blue-600 rounded-xl' : 'bg-transparent rounded hover:bg-gray-200'}`}
                                  onClick={() => handleActivitySourceChange('strava')}
                                >
                                  <img src={StravaPoweredLogo2} alt="Strava" className="h-8 lg:h-14 w-auto object-contain lg:-rotate-90" />
                                </button>
                                <button
                                  type="button"
                                  title="Garmin sync"
                                  className={`flex-1 min-h-[36px] lg:w-16 lg:min-h-[44px] px-1 py-1 border-0 rounded-lg flex items-center justify-center overflow-hidden ${activitySource === 'garmin' ? 'bg-blue-600 rounded-xl' : 'bg-transparent hover:bg-gray-200'}`}
                                  onClick={() => handleActivitySourceChange('garmin')}
                                >
                                  <img src={GarminLogo} alt="Garmin" className="h-8 lg:h-14 w-auto object-contain lg:-rotate-90" />
                                </button>
                                <button
                                  type="button"
                                  title="Generate without activities"
                                  className={`flex-1 min-h-[36px] lg:w-16 lg:min-h-[44px] px-1 py-1 border-0 rounded-lg flex items-center justify-center text-[10px] lg:text-xs font-semibold ${activitySource === 'none' ? 'bg-blue-600 text-white rounded-xl' : 'bg-transparent text-gray-700 hover:bg-gray-200'} ${noActivitiesFlashing ? 'animate-tip-flash' : ''}`}
                                  onClick={() => handleActivitySourceChange('none')}
                                >
                                  <span className="whitespace-nowrap lg:[writing-mode:vertical-lr] lg:rotate-180">No activities</span>
                                </button>
                              </div>

                              <div className="flex-1 min-w-0 w-full space-y-0 lg:space-y-4 overflow-y-auto">
                                {activitySource === 'strava' && (
                                  <div className="flex flex-col space-y-4 items-center justify-center">
                                    <StravaComponent
                                      stravaStatus={stravaStatus}
                                      onStatusUpdate={setStravaStatus}
                                      filteredActivities={filteredActivities}
                                      onSetFilteredActivities={handleActivitySelectionChange}
                                      allDates={allDates}
                                      fromDate={fromDate}
                                      toDate={toDate}
                                      onAllDatesChange={setAllDates}
                                      onFromDateChange={setFromDate}
                                      onToDateChange={setToDate}
                                    />
                                  </div>
                                )}

                                {activitySource === 'garmin' && (
                                  <div className="flex space-y-4 justify-center">
                                    <GarminComponent
                                      garminStatus={garminStatus}
                                      onStatusUpdate={setGarminStatus}
                                      filteredActivities={garminActivities}
                                      onSetFilteredActivities={handleActivitySelectionChange}
                                      allDates={allDates}
                                      fromDate={fromDate}
                                      toDate={toDate}
                                      onAllDatesChange={setAllDates}
                                      onFromDateChange={setFromDate}
                                      onToDateChange={setToDate}
                                    />
                                  </div>
                                )}
                                {activitySource !== 'none' && (
                                  <div className="mt-6">
                                    <ActivityFilterPanel
                                      compact
                                      flashDate={dateFilterFlashing}
                                      flashTypes={typeFilterFlashing}
                                      activities={activitiesInView}
                                      activitySettings={settings.activities}
                                      hiddenIds={hiddenActivityIds}
                                      onToggle={(id) =>
                                        setHiddenActivityIds(prev =>
                                          prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
                                        )
                                      }
                                      onToggleType={(type, hideAll) => {
                                        const typeIds = activitiesInView
                                          .filter(a => a.type === type)
                                          .map(a => a.id);
                                        setHiddenActivityIds(prev => {
                                          if (hideAll) {
                                            return Array.from(new Set([...prev, ...typeIds]));
                                          }
                                          return prev.filter(id => !typeIds.includes(id));
                                        });
                                      }}
                                      commutes={activitySource === 'manual' ? undefined : commutes}
                                      onCommutesChange={activitySource === 'manual' ? undefined : setCommutes}
                                      allDates={allDates}
                                      onAllDatesChange={setAllDates}
                                      fromDate={fromDate}
                                      toDate={toDate}
                                      onFromDateChange={setFromDate}
                                      onToDateChange={setToDate}
                                      onDeleteActivity={activitySource === 'manual' ? handleDeleteManualActivity : undefined}
                                      dateRightSlot={activitySource === 'manual' ? (
                                        <GPXUploadHandler compact onUploadComplete={fetchManualActivities} />
                                      ) : null}
                                    />
                                    {/* Inline copy of the Activities tile
                                        from the Customize sheet so the user
                                        can tweak color / opacity / heatmap
                                        palette without leaving the Map
                                        section. ColorSettingsPopup in basic
                                        renderMode renders just the activities
                                        expanded tile inline (no sheet). */}
                                    <div className="mt-1 lg:mt-4">
                                      <ColorSettingsPopup
                                        settings={{ ...settings, renderMode: 'basic' }}
                                        onColorSettingChange={handleColorSettingChange}
                                        onActivityTypeSettingChange={handleActivityTypeSettingChange}
                                        activitySource={activitySource}
                                        triggerButton={false}
                                        syncOceansWater={syncWaterOceans}
                                        onSyncToggle={handleSyncWaterOceans}
                                        selectedCategory="activities"
                                        onSelectedCategoryChange={() => { }}
                                        hideActivitiesToggle
                                        availableActivityTypes={(() => {
                                          // Distinct types present in activitiesInView, in
                                          // first-seen order. We drop a type from the picker
                                          // when every one of its activities is hidden via
                                          // the "Activities in view" type checkbox — those
                                          // tracks won't print, so styling them is moot.
                                          const hidden = new Set(hiddenActivityIds);
                                          const visibleCount = new Map();
                                          const order = [];
                                          for (const a of activitiesInView) {
                                            const t = a.type || 'Other';
                                            if (!visibleCount.has(t)) {
                                              visibleCount.set(t, 0);
                                              order.push(t);
                                            }
                                            if (!hidden.has(a.id)) {
                                              visibleCount.set(t, visibleCount.get(t) + 1);
                                            }
                                          }
                                          return order.filter((t) => visibleCount.get(t) > 0);
                                        })()}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <FormatSelectorPopup
                      isOpen={isFormatSelectorOpen}
                      onClose={() => setIsFormatSelectorOpen(false)}
                      onFormatSelect={handleFormatSelect}
                      selectedFormat={selectedFormat}
                      isPortrait={isPortrait}
                    />
                  </div>
                )}
                {activeMapSection === 'frame' && (
                  <div className="flex flex-col h-full p-4 lg:p-8 items-center w-full">
                    {/* <h2 className="text-3xl font-bold text-center mb-8">Add a Frame</h2> */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center">
                      {/* Frame settings */}
                      <div className="space-y-2 lg:space-y-4">
                        <div className="flex items-center justify-center lg:mb-10">
                          {/* <Button
                            onClick={toggleFrame}
                            variant="black"
                            className="px-4 py-2 mb-4"
                          >
                            {addFrame ? 'Remove custom frame' : 'Add custom frame'}
                          </Button> */}
                          <div className='flex gap-2 items-center'>
                            <Checkbox
                              id="customFrame"
                              checked={addFrame}
                              onCheckedChange={(checked) => toggleFrame()}
                              className="h-6 w-6 border-black rounded-xl"
                            />
                            <p className='font-bold text-sm lg:text-lg'>
                              Add a Frame
                            </p>
                          </div>
                        </div>
                        <div className={`${!addFrame ? 'opacity-80 pointer-events-none' : ''}`}>
                          <IntegratedFrameSettings
                            frameSettings={frameSettings}
                            onSettingsChange={handleFrameSettingsChange}
                            disabled={!addFrame}
                          />
                        </div>
                      </div>
                      {/* Frame preview */}
                      <div className="flex justify-center items-center relative">
                        {addFrame && frameImage ? (
                          <div className="shadow-2xl overflow-hidden relative">
                            <img
                              src={frameImage}
                              alt="Frame preview"
                              className="max-w-full max-h-[600px]"
                            />
                            {isLoadingFrame && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                                <Loader2 className="w-8 h-8 animate-spin text-white" />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="shadow-2xl overflow-hidden">
                            <img
                              src={borderExample}
                              alt="Border example"
                              className="max-w-full max-h-[600px] opacity-40"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {activeMapSection === 'preview' && (
                  <div className="flex flex-col h-full p-4 lg:p-8 items-center w-full">
                    {/* <h2 className="text-3xl font-bold text-center mb-6">Preview and Checkout</h2> */}
                    <div className="flex flex-col items-center space-y-4 w-full">
                      <div className="flex flex-col items-center w-full gap-3">
                        {/* Generate + Abort share the same total width as
                            the Generate button alone (max-w-xs). When Abort
                            is hidden, Generate flex-1's into the full width;
                            when Abort appears, Generate shrinks to make
                            room and the pair stays inside max-w-xs. */}
                        <div className="flex items-center justify-center gap-2 w-full max-w-xs mx-auto">
                          <Button
                            onClick={generateFinalPlot}
                            disabled={isGenerationActive || !haveSettingsChanged() || previewLoadBlocking}
                            variant="unstyled"
                            className={`
                                relative px-4 py-2 rounded-xl flex-1 min-w-0
                                flex items-center justify-center
                                transition-all duration-3000
                                border-0
                                ${isGenerationActive || !haveSettingsChanged() || previewLoadBlocking
                                ? "bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed shadow-gray-300"
                                : `
                                    bg-gradient-to-r from-black/90 via-black/100 to-black/60
                                    bg-[length:200%_auto]
                                    animate-shine
                                    text-white font-medium
                                    hover:scale-105
                                  `}
                              `}
                          >
                            {isGenerationActive ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Generating...
                              </>
                            ) : !haveSettingsChanged() ? (
                              <>
                                <ImageIcon className="mr-2 h-4 w-4" />
                                No changed settings
                              </>
                            ) : (
                              <>
                                <ImageIcon className="mr-2 h-4 w-4" />
                                Generate map
                              </>
                            )}
                          </Button>
                          {/* Abort sits next to Generate while a render is
                              in flight, sharing the same max-w-xs row. The
                              render unwinds on the next progress checkpoint
                              so cells already fetched stay cached. */}
                          {isGenerationActive && (
                            <Button
                              onClick={abortGeneration}
                              disabled={isAborting}
                              variant="black"
                              className="px-4 py-2 rounded-xl flex-shrink-0 flex items-center justify-center"
                            >
                              {isAborting ? (
                                <>
                                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                  Aborting…
                                </>
                              ) : (
                                <>
                                  <X className="mr-2 h-4 w-4" />
                                  Abort
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                        <GenerationProgress status={generationStatus} />
                      </div>
                      <MobileMapLayout
                        isPortrait={isPortrait}
                        setEnlargedImage={setEnlargedImage}
                        selectedFormat={selectedFormat}
                        handleSizeSelect={handleSizeSelect}
                        handlePrintSelect={handlePrintSelect}
                        isUnlocking={isUnlocking}
                        setVoucher={setVoucher}
                        setSelectedTab={handleTabChange}
                        compareFlashNonce={compareFlashNonce}
                        recentMapsData={recentMapsData}
                        isLoadingRecentMaps={isLoadingRecentMaps}
                        recentMapsError={recentMapsError}
                        refreshRecentMaps={fetchRecentMaps}
                      />
                      <div className="flex flex-col items-center gap-2 pt-6 pb-4 text-center">
                        <p className="text-sm text-gray-600">
                          Enjoying Map Prints? Help others find it.
                        </p>
                        <ReviewForm>
                          <Button variant="outline" size="sm" className="rounded-2xl">
                            <Star className="h-4 w-4 mr-2" />
                            Leave a review
                          </Button>
                        </ReviewForm>
                      </div>
                    </div>
                    {enlargedImage && (
                      <div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2"
                        onClick={() => setEnlargedImage(null)}
                      >
                        <div
                          className="bg-white p-3 rounded-2xl relative w-[98vw] max-w-[1100px] max-h-[90vh] flex flex-col"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex justify-between items-center mb-2 flex-shrink-0">
                            <h3 className="text-lg font-semibold">Preview of your map</h3>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setEnlargedImage(null)}
                              className="text-gray-500 hover:text-gray"
                            >
                              <X className="h-6 w-6" />
                            </Button>
                          </div>
                          <img
                            src={enlargedImage.imageUrl}
                            alt={`Enlarged preview for ${enlargedImage.name}`}
                            className="flex-1 min-h-0 w-full object-contain shadow-2xl shadow-black/50"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Section-aware Tipps overlay. Lives at the create-tab level so
                a single Tipps button (above the section content) can open
                contextual hints for whichever section is active. */}
            {tippsOpen && (
              <TippsOverlay
                section={activeMapSection}
                onClose={() => setTippsOpen(false)}
                onNavigate={handleSectionChange}
                onFlashMap={(name) => setTimeout(() => flashMapControl(name), 150)}
                onFlashCompare={() => setTimeout(() => setCompareFlashNonce((n) => n + 1), 150)}
                onShowActivityPanel={flashPlotActivities}
                onShowActivityCustomize={() => {
                  // Switch to the Style section, open Customize, and jump
                  // straight to the Activities category. Mirrors what the
                  // category card click does in the style page.
                  handleSectionChange('style');
                  setExpandedCategory('activities');
                  setCustomizeOpen(true);
                }}
                onFlashActivityFilter={flashActivityFilter}
                // hasActivities drives the "is the Width / Find /
                // Heatmap-palette tip relevant?" dim. We treat the user
                // as having activities only if all three things hold:
                //   1. an activity source is picked (not 'none'),
                //   2. the activities layer toggle is on, and
                //   3. there are visible activities on the map.
                // Any one of those off → the indented tips below "Add
                // activities…" are moot, so they grey out.
                hasActivities={
                  activitySource !== 'none'
                  && settings?.activities?.active !== false
                  && basicPreviewActivities.length > 0
                }
                activitySettings={settings?.activities}
                visibleActivityCount={basicPreviewActivities.length}
              />
            )}
          </TabsContent>

          {/* <TabsContent value="history" className="flex-1 h-[calc(100vh-16rem)] overflow-y-auto"> */}
          <TabsContent value="history" className="flex-1 h-full overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0">
              <ImageSlideshow
                // Pass the lifted state
                images={images}
                setImages={setImages}
                imageUrls={imageUrls}
                setImageUrls={setImageUrls}
                onSettingsChange={(newSettings) => {
                  setSettings(prevSettings => ({
                    ...prevSettings,
                    ...newSettings
                  }));
                }}
              />
            </div>
            <div className="flex-shrink-0 py-3 flex justify-center bg-white border-t border-gray-200">
              <Button
                onClick={() => handleTabChange('create')}
                className="rounded-2xl bg-gray-900 border-0 text-white hover:bg-gray-600 px-6 py-2 shadow-md"
              >
                <ArrowLeft className="mr-2 h-5 w-5" /> Back to Create Map
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="admin" className="flex-1 h-[calc(100vh-16rem)]">
            <div className="p-6 h-full flex flex-col min-h-0">
              <div className="mb-4 flex-shrink-0">
                <Button
                  onClick={() => handleTabChange('create')}
                  size="sm"
                  className="rounded-2xl bg-gray-900 border-0 text-white hover:bg-gray-600"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back to Create Map
                </Button>
              </div>
              <div className="flex-1 min-h-0">
                <AdminPanel />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
